import { Container, Graphics, Sprite, Text, type FederatedPointerEvent, type Texture } from 'pixi.js';
import type { Locale, ThemePack } from '@holdline/content';
import {
  canBuildAt,
  cheapestPrice,
  closedCells,
  computeMods,
  effectiveStats,
  nextTrait,
  previewPaths,
  rerollPrice,
  sellValue,
  unitPrice,
  units,
  wallRepairPrice,
  waveGold,
  xpCost,
  type ArchetypeId,
  type Cell,
  type EnemyArchId,
  type HitSource,
  type PathPreview,
  type Phase,
  type Unit,
  starsFor,
  traitGainedAt,
  unitTraits,
  waveFor,
} from '@holdline/sim';
import type { ChallengeResult, SubmitResult } from './api';
import { art } from './art';
import type { Game } from './game';
import { BLEED, COLORS, FIELD_PALETTES, L, RARITY_COLORS, hexToNum, type FieldPalette } from './layout';
import { music } from './music';
import { sound, type Sfx } from './sound';
import { drawBuilding, drawBunker, drawDirtTile, drawEnemy, drawGlyph, drawGrassTile, drawHero, drawRock, drawSoldier, mixColor } from './sprites';
import { haptic } from './telegram';

type T = (key: string, vars?: Record<string, string | number>) => string;

export type SubmissionStatus =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; result: SubmitResult }
  | { kind: 'challenge'; result: ChallengeResult };

export interface ViewHooks {
  canShare: () => boolean;
  onShare: () => void;
  onMenu: () => void;
  /** Set while playing a weekly challenge: its display name. */
  challengeName: string | null;
  onLeaveChallenge: () => void;
  /** Ladder level being played, or null on an open map. */
  levelNumber: number | null;
  onNextLevel: (mapId: string) => void;
  /** Field skin id from the profile; unknown ids fall back to the default palette. */
  skin: () => string;
}

interface DragSource {
  kind: 'shop' | 'cell' | 'pocket';
  slot?: number;
  cell?: Cell;
  pocket?: number;
  /** What is being dragged: level 1 for a shop slot, the real building otherwise. */
  unit: Unit;
  ghost: Container;
  moved: boolean;
  sellHint?: Container | undefined;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  ttl: number;
  color: number;
  size: number;
  /** Pixels per second squared pulled downward; smoke uses a negative value and drifts up. */
  gravity?: number;
  /** Size multiplier at the end of life; smoke puffs grow, sparks shrink. */
  grow?: number;
  /** Peak alpha; smoke stays translucent. */
  alpha?: number;
}

interface Flash {
  fx: number;
  fy: number;
  tx: number;
  ty: number;
  color: number;
  life: number;
  ttl: number;
  splashPx: number;
}

interface Floater {
  text: Text;
  life: number;
  ttl: number;
}

interface Tween {
  target: Container;
  from: number;
  to: number;
  life: number;
  ttl: number;
}

type CoachStep = 'place_first' | 'place_second' | 'fight' | 'clear' | 'merge' | 'perk' | 'done';

interface CoachState {
  step: CoachStep;
  /** Timed steps end on their own. */
  ttl: number;
  /** Rects the highlight rings sit on and the arrow points at, in logical pixels. */
  targets: { x: number; y: number; w: number; h: number }[];
  text: string;
  /** Set once the merge step has been offered, so it is not repeated after every wave. */
  mergeShown: boolean;
  perkShown: boolean;
  /** Set once the "rocks can be cleared" step has been shown after the first wave. */
  clearShown: boolean;
}

const COACH_KEY = 'holdline.coach';

/** A short centred announcement over the board: wave start, wave cleared, boss. */
interface Banner {
  node: Container;
  life: number;
  ttl: number;
  baseY: number;
}

/** Blend two 0xRRGGBB colours; k = 0 gives a, k = 1 gives b. */
function mix(a: number, b: number, k: number): number {
  const ch = (shift: number): number => {
    const x = (a >> shift) & 255;
    const y = (b >> shift) & 255;
    return Math.round(x + (y - x) * k) & 255;
  };
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
}

const SHOT_SFX: Partial<Record<ArchetypeId, Sfx>> = { rapid: 'shot_rapid', splash: 'shot_splash', burn: 'shot_burn', chain: 'shot_chain' };
const DIE_PITCH: Record<EnemyArchId, number> = { walker: 1, runner: 1.4, brute: 0.6, boss: 0.4, flyer: 1.7, armored: 0.75 };

const FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const INK = COLORS.ink;

function label(text: string, size: number, color: number, weight: 'normal' | 'bold' = 'normal'): Text {
  return new Text({ text, style: { fill: color, fontSize: size, fontFamily: FONT, fontWeight: weight } });
}

/** Outlined label: readable on any tile colour. */
function tileLabel(text: string, size: number): Text {
  return new Text({
    text,
    style: { fill: 0xffffff, fontSize: size, fontFamily: FONT, fontWeight: 'bold', stroke: { color: INK, width: 3 } },
  });
}

function centerIn(t: Text, x: number, y: number, w: number, h: number): void {
  t.anchor.set(0.5);
  t.x = x + w / 2;
  t.y = y + h / 2;
}

function cellKey(c: Cell): string {
  return `${c.x},${c.y}`;
}

/**
 * Cartoon vector placeholder renderer. One board: enemies walk down it, buildings stand on it.
 * Everything visual reads from the theme pack, so a sprite pack later slots in without touching logic.
 */
export class View {
  readonly root = new Container();
  private readonly bg = new Graphics();
  private readonly grid = new Container();
  private readonly field = new Container();
  /** Bitmap bodies of enemies, soldiers and the hero when an art pack has them; vector bodies stay in fieldFx. */
  private readonly fieldSprites = new Container();
  private fieldSpriteMap = new Map<string, Sprite>();
  /** Base scale and birth time per field sprite, for squash-and-stretch and the spawn pop. */
  private spriteBase = new Map<string, number>();
  private spriteBorn = new Map<string, number>();
  /** Field sprites driven by a frame set (their scale is the set's, not a contain fit). */
  private spriteAnimated = new Set<string>();
  /** Last swing phase per actor and until when the strike frame is held, so a blow reads even at x3. */
  private prevSwing = new Map<string, number>();
  private strikeUntil = new Map<string, number>();
  private readonly fieldFx = new Graphics();
  private readonly floaterLayer = new Container();
  private readonly strip = new Container();
  private readonly wall = new Container();
  private readonly shop = new Container();
  private readonly buttons = new Container();
  private readonly hud = new Container();
  private readonly dragLayer = new Container();
  private readonly bannerLayer = new Container();
  /** Guided first run: highlights, an arrow and one line of text. Never takes input. */
  private readonly coachLayer = new Container();
  private coach: CoachState | null = null;
  private readonly overlay = new Container();
  private readonly introLayer = new Container();
  private readonly toastLayer = new Container();

  private drag: DragSource | null = null;
  /** Cell currently hovered while dragging, for the live corridor preview. */
  private hoverKey: string | null = null;
  /** Pocket slot under the pointer while dragging, and the ring drawn on it. */
  private hoverPocket: number | null = null;
  private pocketMark: Graphics | null = null;
  /** A re-render asked for during a pointer event; done on the next frame so the tap on a rebuilt node still lands. */
  private renderQueued = false;
  private selectedCell: Cell | null = null;
  private cellNodes = new Map<string, Container>();
  private toast: Text | null = null;
  private toastTimer = 0;
  private particles: Particle[] = [];
  private flashes: Flash[] = [];
  private floaters: Floater[] = [];
  private floaterPool: Text[] = [];
  private tweens: Tween[] = [];
  private submission: SubmissionStatus = { kind: 'idle' };
  private paths: PathPreview = { spawnColumns: [], routes: [], breakCells: [] };
  private bunkerNode: Container | null = null;
  private bunkerRect: { x: number; y: number; w: number; h: number } | null = null;

  // ---- battle feel: all presentation, none of it touches the sim ----
  /** Render clock in ms, for bobbing and pulsing. */
  private now = 0;
  /** Enemy id -> ms of white flash left after a hit. */
  private enemyFlash = new Map<number, number>();
  /** Grid index -> ms of red flash left after an enemy chewed on that building. */
  private cellFlash = new Map<number, number>();
  private bunkerFlash = 0;
  private heroFlash = 0;
  private shake = { amp: 0, life: 0, ttl: 0 };
  private banners: Banner[] = [];
  private knownEnemies = new Set<number>();
  private lowHpBeat = 0;
  private ambientAcc = 0;
  private smokeAcc = 0;
  private lastPhase: Phase;
  private lastWave: number;
  private goldAtFight = 0;

  constructor(
    private readonly game: Game,
    private readonly theme: ThemePack,
    private readonly locale: Locale,
    private readonly t: T,
    private readonly hooks: ViewHooks,
  ) {
    this.root.addChild(
      this.bg,
      this.grid,
      this.field,
      this.strip,
      this.wall,
      this.shop,
      this.buttons,
      this.hud,
      this.dragLayer,
      this.bannerLayer,
      this.coachLayer,
      this.overlay,
      this.introLayer,
      this.toastLayer,
    );
    this.lastPhase = game.state.phase;
    this.lastWave = game.state.wave;
    music.setTrack(game.state.phase === 'battle' ? 'battle' : 'build');
    const fieldMask = new Graphics().rect(0, L.gridY - 10, L.W, this.boardHeight() + 14).fill(0xffffff);
    this.field.addChild(fieldMask, this.fieldSprites, this.fieldFx, this.floaterLayer);
    this.field.mask = fieldMask;
    // Decorative layers must never take part in hit testing. Pixi tests passive children of an
    // interactive parent too, and a passive shape that contains the point resolves the hit to the
    // root instead of the building under it: the range circle used to swallow every tap inside it.
    for (const layer of [this.bg, this.field, this.dragLayer, this.bannerLayer, this.coachLayer, this.toastLayer]) layer.eventMode = 'none';

    this.root.eventMode = 'static';
    this.root.hitArea = { contains: (x: number, y: number) => x >= 0 && y >= 0 && x <= L.W && y <= L.H };
    this.root.on('pointermove', (e) => this.onMove(e));
    this.root.on('pointerup', (e) => this.onUp(e));
    this.root.on('pointerupoutside', (e) => this.onUp(e));

    this.drawBackground();
    this.renderAll();
    game.on((e) => {
      if (e.kind === 'state') {
        if (game.state.phase !== 'placing') this.selectedCell = null;
        this.onPhaseChange();
        this.renderAll();
        this.updateCoach();
        if (game.state.phase === 'won') haptic.success();
        else if (game.state.phase === 'lost') haptic.error();
      } else if (e.kind === 'battle_tick') {
        this.collectBattleEffects();
        if (game.battle && game.battle.lastStructureLosses.length > 0) {
          this.paths = game.battle.paths;
          this.renderGrid();
          haptic.error();
        } else if (this.bunkerNode && game.battle && game.battle.tick % 10 === 0) {
          // Keep the bunker's HP bar fresh without rebuilding the whole grid every tick.
          this.renderGrid();
        }
        this.renderWall();
        this.renderHud();
        this.renderStrip();
      } else if (e.kind === 'error') {
        haptic.error();
        sound.play('error');
        this.showToast(this.errorText(e.code));
      }
    });
  }

  /** Wave start and wave end announcements, victory and defeat jingles. Runs before the re-render. */
  private onPhaseChange(): void {
    const s = this.game.state;
    const prev = this.lastPhase;
    music.setTrack(s.phase === 'battle' ? 'battle' : s.phase === 'won' || s.phase === 'lost' ? null : 'build');
    if (s.phase === 'battle' && prev !== 'battle') {
      this.goldAtFight = s.gold;
      this.knownEnemies.clear();
      this.enemyFlash.clear();
      this.cellFlash.clear();
      const preview = this.game.wavePreview();
      const boss = preview.some((p) => p.arch === 'boss');
      const isLast = !s.endless && s.wave >= s.totalWaves;
      const title = s.endless ? this.t('banner_wave', { n: s.wave }) : this.t('banner_wave_of', { n: s.wave, total: s.totalWaves });
      const sub = boss ? this.t('banner_boss') : isLast ? this.t('banner_last_wave') : undefined;
      this.banner(title, COLORS.text, sub, boss ? COLORS.danger : COLORS.gold, 1500);
      sound.play('wave_start');
    } else if (prev === 'battle' && (s.phase === 'placing' || s.phase === 'perk')) {
      const cleared = s.wave - 1;
      const earned = s.gold - this.goldAtFight;
      this.banner(this.t('banner_cleared', { n: cleared }), COLORS.hp, earned > 0 ? this.t('banner_gold', { n: earned }) : undefined, COLORS.gold, 1700);
      sound.play('wave_clear');
    } else if (s.phase === 'won' && prev !== 'won') {
      sound.play('win');
      this.startShake(4, 500);
    } else if (s.phase === 'lost' && prev !== 'lost') {
      sound.play('lose');
      this.startShake(7, 600);
    }
    if (s.phase === 'perk' && prev !== 'perk') sound.play('perk');
    this.lastPhase = s.phase;
    this.lastWave = s.wave;
  }

  private startShake(amp: number, ttl: number): void {
    if (this.shake.life < this.shake.ttl && this.shake.amp * (1 - this.shake.life / this.shake.ttl) > amp) return;
    this.shake = { amp, life: 0, ttl };
  }

  private banner(text: string, color: number, sub?: string, subColor: number = COLORS.gold, ttl = 1400): void {
    const c = new Container();
    c.eventMode = 'none';
    const h = sub ? 70 : 50;
    const w = 280;
    c.addChild(new Graphics().roundRect(-w / 2, -h / 2, w, h, 16).fill({ color: 0x000000, alpha: 0.58 }).stroke({ width: 2, color, alpha: 0.6 }));
    const tx = tileLabel(text, 24);
    tx.style.fill = color;
    tx.anchor.set(0.5);
    tx.y = sub ? -11 : 0;
    c.addChild(tx);
    if (sub) {
      const st = tileLabel(sub, 15);
      st.style.fill = subColor;
      st.anchor.set(0.5);
      st.y = 15;
      c.addChild(st);
    }
    // Stack under whatever is still showing so two announcements never overlap.
    const live = this.banners.filter((b) => b.life < b.ttl * 0.8).length;
    const baseY = L.gridY + this.boardHeight() * 0.42 + live * 78;
    c.x = L.W / 2;
    c.y = baseY;
    c.scale.set(0.5);
    c.alpha = 0;
    this.bannerLayer.addChild(c);
    this.banners.push({ node: c, life: 0, ttl, baseY });
  }

  // ---------- public ----------

  private errorText(code: string): string {
    if (code === 'no_gold') return this.t('not_enough_gold');
    if (code === 'no_build_zone') return this.t('no_build_zone');
    if (code === 'permanent') return this.t('permanent');
    if (code === 'no_repair') return this.t('no_repair');
    if (code === 'pocket_full') return this.t('pocket_full');
    if (code === 'pocket_empty') return this.t('pocket_empty');
    return code;
  }

  // ---------- coach: guided first run ----------

  /** Start the guided first run unless it was completed before (or `force`). */
  startCoach(force = false): void {
    let done = false;
    try {
      done = localStorage.getItem(COACH_KEY) === 'done';
    } catch {
      /* fine */
    }
    if (done && !force) return;
    if (this.game.state.modifiers.length > 0) return;
    this.coach = { step: 'place_first', ttl: 0, targets: [], text: '', mergeShown: false, perkShown: false, clearShown: false };
    this.updateCoach();
  }

  private finishCoach(): void {
    this.coach = null;
    this.coachLayer.removeChildren();
    try {
      localStorage.setItem(COACH_KEY, 'done');
    } catch {
      /* fine */
    }
  }

  /** A free buildable cell next to the enemy road but not on it, nearest the base; falls back to any free cell. */
  private suggestCell(avoid?: Cell): Cell | null {
    const s = this.game.state;
    const route = new Set<number>();
    for (const r of this.paths.routes) for (const i of r) route.add(i);
    const mid = (s.width - 1) / 2;
    let best: Cell | null = null;
    let bestScore = -Infinity;
    let fallback: Cell | null = null;
    for (let y = 0; y < s.height; y++) {
      for (let x = 0; x < s.width; x++) {
        const c = { x, y };
        if (avoid && avoid.x === x && avoid.y === y) continue;
        const idx = y * s.width + x;
        if (s.grid[idx]!.kind !== 'empty' || !canBuildAt(s, c) || route.has(idx)) continue;
        if (!fallback || y > fallback.y) fallback = c;
        const nearRoad = [idx - 1, idx + 1, idx - s.width, idx + s.width].some((n) => n >= 0 && n < s.grid.length && route.has(n) && Math.abs((n % s.width) - x) <= 1);
        if (!nearRoad) continue;
        const score = y * 10 - Math.abs(x - mid);
        if (score > bestScore) {
          bestScore = score;
          best = c;
        }
      }
    }
    return best ?? fallback;
  }

  /** Re-evaluate the coach against the current state; called after every state change. */
  private updateCoach(): void {
    const c = this.coach;
    if (!c) return;
    const s = this.game.state;
    if (s.phase === 'won' || s.phase === 'lost') {
      this.finishCoach();
      return;
    }
    const unitList = [...units(s)];
    const count = unitList.length;
    const price = cheapestPrice(s, this.game.balance);

    // Perk draft: one line over the cards, first time only.
    if (s.phase === 'perk') {
      if (!c.perkShown) {
        c.perkShown = true;
        this.setCoach('perk', [], this.t('coach_perk'), 0);
      }
      return;
    }
    if (s.phase === 'battle') {
      this.coachLayer.removeChildren();
      c.targets = [];
      return;
    }
    if (s.phase !== 'placing') return;

    // Wave 1: place, place, fight.
    if (s.wave === 1) {
      if (count === 0 && s.gold >= price) {
        const cell = this.suggestCell();
        const targets = [this.shopRect(0)];
        if (cell) targets.push(this.cellRect(cell));
        this.setCoach('place_first', targets, this.t('coach_place_first'), 0);
        return;
      }
      if (count === 1 && s.gold >= price) {
        const cell = this.suggestCell(unitList[0]!.cell);
        const targets = [this.shopRect(0)];
        if (cell) targets.push(this.cellRect(cell));
        this.setCoach('place_second', targets, this.t('coach_place_second'), 0);
        return;
      }
      this.setCoach('fight', [{ x: 14, y: L.buttonsY, w: 196, h: L.buttonH }], this.t('coach_fight'), 0);
      return;
    }

    // After the first cleared wave: space is the constraint, so show that rocks can be cleared, then merging.
    if (!c.clearShown) {
      c.clearShown = true;
      let rock: Cell | null = null;
      let rockCost = Infinity;
      for (const cell of closedCells(s)) {
        const cs = s.grid[cell.y * s.width + cell.x]!;
        if (cs.kind === 'closed' && cs.cost < rockCost) {
          rockCost = cs.cost;
          rock = cell;
        }
      }
      if (rock) {
        this.setCoach('clear', [this.cellRect(rock)], this.t('coach_clear'), 3600);
        return;
      }
    }
    if (c.step === 'clear' && c.ttl > 0) return;
    // The first merge, whenever it happens (a perk draft may have opened in between), ends the lesson.
    if (s.merges > 0) {
      if (c.step !== 'done') this.setCoach('done', [], this.t('coach_done'), 2600);
      return;
    }
    if (!c.mergeShown) {
      // Two equal buildings on the board: point at both. Else a shop item matching a board building.
      for (let i = 0; i < unitList.length; i++) {
        for (let j = i + 1; j < unitList.length; j++) {
          const a = unitList[i]!;
          const b = unitList[j]!;
          if (a.unit.arch === b.unit.arch && a.unit.level === b.unit.level) {
            c.mergeShown = true;
            this.setCoach('merge', [this.cellRect(a.cell), this.cellRect(b.cell)], this.t('coach_merge'), 0);
            return;
          }
        }
      }
      if (s.gold >= price) {
        for (let slot = 0; slot < s.shop.length; slot++) {
          const arch = s.shop[slot]!;
          const on = unitList.find((u) => u.unit.arch === arch && u.unit.level === 1);
          if (on) {
            c.mergeShown = true;
            this.setCoach('merge', [this.shopRect(slot), this.cellRect(on.cell)], this.t('coach_merge_buy'), 0);
            return;
          }
        }
      }
      if (s.wave >= 4) this.finishCoach();
      else {
        this.coachLayer.removeChildren();
        c.targets = [];
      }
      return;
    }
    if (c.step === 'merge') return; // targets stay until the merge happens or the run moves on
    // Nothing to teach right now (e.g. back from the perk draft): keep the screen clean.
    this.coachLayer.removeChildren();
    c.targets = [];
  }

  private setCoach(step: CoachStep, targets: CoachState['targets'], text: string, ttl: number): void {
    const c = this.coach;
    if (!c) return;
    c.step = step;
    c.targets = targets;
    c.text = text;
    c.ttl = ttl;
    this.renderCoach();
  }

  private renderCoach(): void {
    const c = this.coach;
    this.coachLayer.removeChildren();
    if (!c || !c.text) return;
    const rings = new Graphics();
    rings.label = 'rings';
    this.coachLayer.addChild(rings);
    const arrow = new Graphics();
    arrow.label = 'arrow';
    this.coachLayer.addChild(arrow);
    // Text bubble: over the top rows of the board, where nothing is tapped while placing.
    const bubble = new Container();
    const tx = label(c.text, 12.5, COLORS.text, 'bold');
    tx.style.wordWrap = true;
    tx.style.wordWrapWidth = L.W - 76;
    tx.style.align = 'center';
    tx.anchor.set(0.5, 0);
    const bw = Math.ceil(tx.width) + 28;
    const bh = Math.ceil(tx.height) + 18;
    bubble.addChild(new Graphics().roundRect(-bw / 2, 0, bw, bh, 12).fill({ color: 0x1b1e26, alpha: 0.94 }).stroke({ width: 2, color: COLORS.gold }));
    tx.y = 9;
    bubble.addChild(tx);
    bubble.x = L.W / 2;
    bubble.y = c.step === 'perk' ? 66 : c.step === 'fight' ? L.stripY - bh - 6 : L.gridY + 6;
    this.coachLayer.addChild(bubble);
    this.tickCoach(0);
  }

  private tickCoach(deltaMs: number): void {
    const c = this.coach;
    if (!c) return;
    if (c.ttl > 0) {
      c.ttl -= deltaMs;
      if (c.ttl <= 0) {
        c.ttl = 0;
        if (c.step === 'done') this.finishCoach();
        else this.updateCoach();
        return;
      }
    }
    const rings = this.coachLayer.getChildByLabel('rings') as Graphics | null;
    const arrow = this.coachLayer.getChildByLabel('arrow') as Graphics | null;
    if (!rings || !arrow) return;
    const pulse = 0.5 + 0.5 * Math.sin(this.now / 220);
    rings.clear();
    arrow.clear();
    for (const r of c.targets) {
      rings.roundRect(r.x - 3, r.y - 3, r.w + 6, r.h + 6, 10).stroke({ width: 3, color: COLORS.gold, alpha: 0.55 + 0.45 * pulse });
      rings.roundRect(r.x - 7 - pulse * 3, r.y - 7 - pulse * 3, r.w + 14 + pulse * 6, r.h + 14 + pulse * 6, 12).stroke({ width: 2, color: COLORS.gold, alpha: 0.35 * (1 - pulse) });
    }
    // The arrow bobs above the last target (the place to act on).
    const t = c.targets[c.targets.length - 1];
    if (t) {
      const ax = t.x + t.w / 2;
      const ay = t.y - 10 - Math.abs(Math.sin(this.now / 260)) * 8;
      arrow.poly([ax - 11, ay - 22, ax + 11, ay - 22, ax, ay - 4]).fill(COLORS.gold).stroke({ width: 2, color: INK, join: 'round' });
      arrow.rect(ax - 5, ay - 36, 10, 14).fill(COLORS.gold).stroke({ width: 2, color: INK });
    }
  }

  /** First-run explainer, three lines and a button. */
  showIntro(onClose: () => void): void {
    this.introLayer.removeChildren();
    const dim = new Graphics().rect(-BLEED, -BLEED, L.W + BLEED * 2, L.H + BLEED * 2).fill({ color: 0x000000, alpha: 0.8 });
    dim.eventMode = 'static';
    this.introLayer.addChild(dim);
    const card = new Container();
    card.x = 20;
    card.y = 150;
    const w = L.W - 40;
    card.addChild(new Graphics().roundRect(0, 0, w, 330, 14).fill(COLORS.panel).stroke({ width: 2, color: COLORS.accent }));
    const title = tileLabel(this.t('intro_title'), 20);
    centerIn(title, 0, 14, w, 30);
    card.addChild(title);
    ['intro_1', 'intro_2', 'intro_3'].forEach((k, i) => {
      const n = tileLabel(String(i + 1), 16);
      n.style.fill = COLORS.accent;
      n.x = 18;
      n.y = 62 + i * 76;
      const tx = label(this.t(k), 12.5, COLORS.text);
      tx.style.wordWrap = true;
      tx.style.wordWrapWidth = w - 60;
      tx.x = 40;
      tx.y = 60 + i * 76;
      card.addChild(n, tx);
    });
    const ok = this.button(this.t('got_it'), COLORS.button, () => {
      this.introLayer.removeChildren();
      onClose();
    }, 160, 44, 15);
    ok.x = (w - 160) / 2;
    ok.y = 330 - 60;
    card.addChild(ok);
    this.introLayer.addChild(card);
  }

  setSubmission(s: SubmissionStatus): void {
    this.submission = s;
    this.renderOverlay();
  }

  showToast(text: string): void {
    if (this.toast) this.toast.destroy();
    const tx = tileLabel(text, 14);
    tx.style.fill = COLORS.danger;
    centerIn(tx, 0, L.stripY - 26, L.W, 20);
    this.toastLayer.addChild(tx);
    this.toast = tx;
    this.toastTimer = 1400;
  }

  /** Per-frame: effects, tweens, toast. */
  tick(deltaMs: number): void {
    if (this.renderQueued) this.renderAll();
    this.now += deltaMs;
    this.decayFlashes(deltaMs);
    this.tickShake(deltaMs);
    this.tickBanners(deltaMs);
    this.tickCoach(deltaMs);
    this.ambient(deltaMs);
    if (this.toast) {
      this.toastTimer -= deltaMs;
      if (this.toastTimer <= 0) {
        this.toast.destroy();
        this.toast = null;
      }
    }
    for (const tw of this.tweens) {
      tw.life += deltaMs;
      const k = Math.min(1, tw.life / tw.ttl);
      const ease = 1 - (1 - k) * (1 - k);
      if (!tw.target.destroyed) tw.target.scale.set(tw.from + (tw.to - tw.from) * ease);
    }
    this.tweens = this.tweens.filter((tw) => tw.life < tw.ttl && !tw.target.destroyed);

    const dt = deltaMs / 1000;
    for (const p of this.particles) {
      p.life += deltaMs;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += (p.gravity ?? 300) * dt;
    }
    this.particles = this.particles.filter((p) => p.life < p.ttl);
    for (const f of this.flashes) f.life += deltaMs;
    this.flashes = this.flashes.filter((f) => f.life < f.ttl);
    for (const fl of this.floaters) {
      fl.life += deltaMs;
      fl.text.y -= 28 * dt;
      fl.text.alpha = 1 - fl.life / fl.ttl;
    }
    for (const fl of this.floaters.filter((x) => x.life >= x.ttl)) {
      this.floaterLayer.removeChild(fl.text);
      this.floaterPool.push(fl.text);
    }
    this.floaters = this.floaters.filter((fl) => fl.life < fl.ttl);

    if (this.game.battle || this.particles.length || this.flashes.length || this.enemyFlash.size || this.cellFlash.size || this.bunkerFlash > 0) {
      this.renderField();
    }
    if (!this.game.battle && this.fieldSpriteMap.size > 0) {
      for (const sp of this.fieldSpriteMap.values()) sp.destroy();
      this.fieldSpriteMap.clear();
      this.spriteBase.clear();
      this.spriteBorn.clear();
      this.spriteAnimated.clear();
      this.prevSwing.clear();
      this.strikeUntil.clear();
      this.fieldSprites.removeChildren();
    }
  }

  private decayFlashes(deltaMs: number): void {
    for (const [id, ms] of this.enemyFlash) {
      if (ms - deltaMs <= 0) this.enemyFlash.delete(id);
      else this.enemyFlash.set(id, ms - deltaMs);
    }
    for (const [idx, ms] of this.cellFlash) {
      if (ms - deltaMs <= 0) this.cellFlash.delete(idx);
      else this.cellFlash.set(idx, ms - deltaMs);
    }
    this.bunkerFlash = Math.max(0, this.bunkerFlash - deltaMs);
    this.heroFlash = Math.max(0, this.heroFlash - deltaMs);
  }

  /** The board (grid + field) jolts; the HUD and the shop stay put so nothing under a finger moves. */
  private tickShake(deltaMs: number): void {
    const sh = this.shake;
    if (sh.life >= sh.ttl) {
      if (this.grid.x !== 0 || this.grid.y !== 0) {
        this.grid.position.set(0, 0);
        this.field.position.set(0, 0);
      }
      return;
    }
    sh.life += deltaMs;
    const k = Math.max(0, 1 - sh.life / sh.ttl);
    const dx = (Math.random() * 2 - 1) * sh.amp * k;
    const dy = (Math.random() * 2 - 1) * sh.amp * k;
    this.grid.position.set(dx, dy);
    this.field.position.set(dx, dy);
  }

  private tickBanners(deltaMs: number): void {
    for (const b of this.banners) {
      b.life += deltaMs;
      const k = b.life / b.ttl;
      const n = b.node;
      if (k < 0.14) {
        const e = k / 0.14;
        const back = 1 - (1 - e) * (1 - e) * (1 - e);
        n.scale.set(0.5 + 0.6 * back);
        n.alpha = Math.min(1, e * 1.6);
        n.y = b.baseY;
      } else if (k < 0.24) {
        n.scale.set(1.1 - 0.1 * ((k - 0.14) / 0.1));
        n.alpha = 1;
      } else if (k < 0.78) {
        n.scale.set(1);
        n.alpha = 1;
      } else {
        const e = (k - 0.78) / 0.22;
        n.alpha = 1 - e;
        n.y = b.baseY - 26 * e;
      }
    }
    for (const b of this.banners.filter((x) => x.life >= x.ttl)) this.bannerLayer.removeChild(b.node);
    this.banners = this.banners.filter((b) => b.life < b.ttl);
  }

  // ---------- geometry ----------

  private cols(): number {
    return this.game.state.width;
  }

  private rows(): number {
    return this.game.state.height;
  }

  /**
   * Cell size fitted to the board: 54 px for the classic 6×8, smaller for the 6×9 and 7×9 boards of the
   * later chapters, a little larger for the 5×7 opening chapter. Everything on the board scales with it.
   */
  private cellPx(): number {
    const cols = this.cols();
    const rows = this.rows();
    const byWidth = Math.floor((L.W - 26 - (cols - 1) * L.gap) / cols);
    const byHeight = Math.floor((L.stripY - L.gridY - 8 - (rows - 1) * L.gap) / rows);
    return Math.max(36, Math.min(60, byWidth, byHeight));
  }

  /** Scale factor for sprites and radii tuned on 54 px cells. */
  private cellK(): number {
    return this.cellPx() / 54;
  }

  private boardWidth(): number {
    return this.cols() * this.cellPx() + (this.cols() - 1) * L.gap;
  }

  private boardHeight(): number {
    return this.rows() * this.cellPx() + (this.rows() - 1) * L.gap;
  }

  private originX(): number {
    return Math.floor((L.W - this.boardWidth()) / 2);
  }

  private cellRect(c: Cell): { x: number; y: number; w: number; h: number } {
    const cell = this.cellPx();
    return { x: this.originX() + c.x * (cell + L.gap), y: L.gridY + c.y * (cell + L.gap), w: cell, h: cell };
  }

  private cellAtPoint(x: number, y: number): Cell | null {
    const s = this.game.state;
    for (let cy = 0; cy < s.height; cy++) {
      for (let cx = 0; cx < s.width; cx++) {
        const r = this.cellRect({ x: cx, y: cy });
        if (x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h) return { x: cx, y: cy };
      }
    }
    return null;
  }

  /** Milli-cells to pixels; the same scale on both axes now that the board is one grid. */
  private ppm(): number {
    return (this.cellPx() + L.gap) / 1000;
  }

  private fieldPos(mx: number, my: number): { x: number; y: number } {
    return { x: this.originX() + mx * this.ppm() - L.gap / 2, y: L.gridY + my * this.ppm() - L.gap / 2 };
  }

  private shopRect(i: number): { x: number; y: number; w: number; h: number } {
    return { x: 8 + i * (L.shopCell + L.shopGap), y: L.shopY, w: L.shopCell, h: L.shopCell };
  }

  private rerollRect(): { x: number; y: number; w: number; h: number } {
    const n = this.game.balance.shopSize;
    return { x: 8 + n * (L.shopCell + L.shopGap) + 2, y: L.shopY, w: L.rerollW, h: L.shopCell };
  }

  /** Pocket slots sit at the right end of the plank, a little smaller than the shop slots. */
  private pocketRect(i: number): { x: number; y: number; w: number; h: number } {
    const n = this.game.state.pocket.length;
    const total = n * L.pocketCell + (n - 1) * L.shopGap;
    const x0 = L.W - 8 - total;
    return { x: x0 + i * (L.pocketCell + L.shopGap), y: L.shopY + (L.shopCell - L.pocketCell) / 2, w: L.pocketCell, h: L.pocketCell };
  }

  private pocketAtPoint(x: number, y: number): number | null {
    for (let i = 0; i < this.game.state.pocket.length; i++) {
      const r = this.pocketRect(i);
      if (x >= r.x - 3 && x <= r.x + r.w + 3 && y >= r.y - 8 && y <= r.y + r.h + 8) return i;
    }
    return null;
  }

  // ---------- rendering ----------

  private palette(): FieldPalette {
    return FIELD_PALETTES[this.hooks.skin()] ?? FIELD_PALETTES['default']!;
  }

  /** Redraw everything that carries the field palette: after a skin change. */
  reskin(): void {
    this.drawBackground();
    this.renderGrid();
  }

  private drawBackground(): void {
    const g = this.bg;
    const pal = this.palette();
    g.clear();
    g.rect(-BLEED, -BLEED, L.W + BLEED * 2, L.H + BLEED * 2).fill(pal.water);
    // Waves, across the bleed too
    for (let y = -BLEED + 30; y < L.H + BLEED; y += 52) {
      g.moveTo(-BLEED, y)
        .lineTo(L.W + BLEED, y + 6)
        .stroke({ width: 3, color: pal.waterDeep, alpha: 0.35 });
    }
    // Island under the board and the info strip
    const pad = 9;
    // The island: a sandy shore under the lawn instead of a hard dark outline, plus a drop shadow into the water.
    g.roundRect(this.originX() - pad + 2, L.gridY - pad + 6, this.boardWidth() + pad * 2, this.boardHeight() + pad * 2, 18).fill({ color: 0x1b3f66, alpha: 0.35 });
    g.roundRect(this.originX() - pad, L.gridY - pad, this.boardWidth() + pad * 2, this.boardHeight() + pad * 2, 18).fill(pal.shore);
    g.roundRect(this.originX() - pad + 4, L.gridY - pad + 4, this.boardWidth() + pad * 2 - 8, this.boardHeight() + pad * 2 - 8, 14).fill(pal.island);
    // Wooden UI plank for shop and buttons; it runs off the bottom so the band below the canvas is wood too
    g.roundRect(6, L.shopY - 10, L.W - 12, L.H - L.shopY + 4 + BLEED, 14).fill(COLORS.wood).stroke({ width: 3, color: 0x4a3320 });
    g.rect(6, L.shopY - 10, L.W - 12, 6).fill({ color: 0xffffff, alpha: 0.08 });
  }

  renderAll(): void {
    this.renderQueued = false;
    if (this.game.state.phase === 'placing' || this.game.state.phase === 'perk') this.paths = this.pathsForSelection();
    else if (this.game.battle) this.paths = this.game.battle.paths;
    this.renderHud();
    this.renderGrid();
    this.renderField();
    this.renderStrip();
    this.renderWall();
    this.renderShop();
    this.renderButtons();
    this.renderOverlay();
  }

  private isClearableRock(cell: Cell): boolean {
    const s = this.game.state;
    const cs = s.grid[cell.y * s.width + cell.x];
    return cs !== undefined && cs.kind === 'closed' && cs.cost > 0 && canBuildAt(s, cell);
  }

  /**
   * The enemy road as it is, or, with a rock selected, as it would be once that rock is cleared:
   * the board shows the consequence of the purchase before the gold is spent.
   */
  private pathsForSelection(): PathPreview {
    const s = this.game.state;
    const sel = this.selectedCell;
    if (sel && this.isClearableRock(sel)) {
      const grid = s.grid.slice();
      grid[sel.y * s.width + sel.x] = { kind: 'empty' };
      return previewPaths({ ...s, grid });
    }
    return previewPaths(s);
  }

  private clearRock(cell: Cell): void {
    const r = this.cellRect(cell);
    this.selectedCell = null;
    if (this.game.act({ t: 'open', cell })) {
      haptic.light();
      sound.play('open_rock');
      this.burst(r.x + r.w / 2, r.y + r.h / 2, COLORS.rock, 12);
    } else this.renderAll();
  }

  private buildingTile(u: Unit, w: number, h: number, showLevel = true): Container {
    const c = new Container();
    const th = this.theme.units[u.arch];
    const color = hexToNum(th.color);
    // Bitmap buildings come with their own base plate and shadow, so they may fill the whole cell.
    const pic = art.sprite(`unit:${u.arch}`, w + 4, h + 4);
    if (pic) {
      pic.x = w / 2;
      pic.y = h / 2 - 4;
      c.addChild(pic);
    } else {
      const g = new Graphics();
      drawBuilding(g, u.arch, color, w, h);
      const spawner = u.arch === 'barracks' || u.arch === 'archery' || u.arch === 'stable';
      drawGlyph(g, u.arch, w / 2, spawner ? h * 0.62 : h * 0.4, h * 0.13);
      c.addChild(g);
    }
    // Level growth: a merged building reads as bigger and richer without new art.
    if (showLevel && u.level >= 2) {
      const body = c.children[0]!;
      const k = 1 + Math.min(4, u.level - 1) * 0.04;
      // The sprite already carries its contain-fit scale: grow relative to it, not from 1.
      body.scale.set(body.scale.x * k, body.scale.y * k);
      if (body instanceof Sprite) body.y -= (u.level - 1) * 1.2;
      else body.position.set((w - w * k) / 2, (h - h * k) / 2);
      const rim = new Graphics();
      const rimColor = u.level >= 6 ? 0xb478ff : u.level >= 4 ? COLORS.gold : 0xc9a24a;
      rim.roundRect(3, h - 12, w - 6, 8, 4).stroke({ width: u.level >= 4 ? 2.5 : 1.5, color: rimColor, alpha: 0.85 });
      c.addChildAt(rim, 0);
      if (u.level >= 4) {
        // A pennant on the roof from level 4.
        const flag = new Graphics();
        flag.moveTo(w - 10, 6).lineTo(w - 10, 20).stroke({ width: 1.5, color: INK });
        flag.poly([w - 10, 6, w - 2, 9, w - 10, 12]).fill(rimColor).stroke({ width: 1, color: INK });
        c.addChild(flag);
      }
      if (u.level >= 6) {
        const aura = new Graphics().roundRect(-2, -2, w + 4, h + 4, 10).stroke({ width: 3, color: 0xb478ff, alpha: 0.45 });
        c.addChildAt(aura, 0);
      }
    }
    const name = tileLabel(th.name[this.locale], 8.5);
    centerIn(name, 0, h - 16, w, 14);
    c.addChild(name);
    // What it gives, on the tile itself: income for depots, soldiers for camps, repair for engineers.
    let hasTag = false;
    {
      const s = this.game.state;
      const st = effectiveStats({ ...u, level: Math.max(1, u.level) }, this.game.balance, computeMods(s.perks), s.cardLevels[u.arch], s.skills);
      let tag: string | null = null;
      let color: number = COLORS.gold;
      hasTag = false;
      if (st.income > 0) tag = `+${st.income} ◆`;
      else if (st.soldier) {
        tag = `${st.spawnMax} ⚔`;
        color = hexToNum(this.theme.soldiers[u.arch]?.color ?? '#8ab4ff');
      } else if (st.heal > 0 && st.healInterval > 0) {
        tag = `+${((st.heal * 20) / st.healInterval).toFixed(0)}${this.t('per_sec_short')}`;
        color = COLORS.hp;
      }
      if (tag) {
        hasTag = true;
        const tg = tileLabel(tag, 9);
        tg.style.fill = color;
        tg.anchor.set(1, 0);
        tg.x = w - 3;
        tg.y = 2;
        c.addChild(tg);
      }
    }
    if (showLevel && u.level > 0) {
      // Stars in full up to three; compact "★N" earlier when a tag shares the top edge.
      const stars = u.level <= (hasTag ? 1 : 3) ? '★'.repeat(u.level) : `★${u.level}`;
      const lv = tileLabel(stars, 10);
      lv.style.fill = COLORS.star;
      lv.anchor.set(0, 0);
      lv.x = 5;
      lv.y = 2;
      c.addChild(lv);
    }
    return c;
  }

  private button(
    text: string,
    color: number,
    onTap: () => void,
    width = 160,
    height: number = L.buttonH,
    fontSize = 16,
  ): Container {
    const c = new Container();
    c.addChild(new Graphics().roundRect(0, 0, width, height, 10).fill(color).stroke({ width: 2, color: INK }));
    c.addChild(new Graphics().roundRect(3, 3, width - 6, height * 0.4, 6).fill({ color: 0xffffff, alpha: 0.1 }));
    const tx = tileLabel(text, fontSize);
    centerIn(tx, 0, 0, width, height);
    c.addChild(tx);
    c.eventMode = 'static';
    c.cursor = 'pointer';
    c.on('pointertap', onTap);
    return c;
  }

  private hudRefs: { wave: Text; gold: Text; speedBtn: Container; speedLabel: Text; bar: Graphics } | null = null;

  /** Built once and updated in place: rebuilding buttons every tick would swallow their taps. */
  private renderHud(): void {
    const s = this.game.state;
    const b = this.game.battle;
    if (!this.hudRefs) {
      // The bar continues above the canvas into the top band of tall screens.
      this.hud.addChild(new Graphics().rect(-BLEED, L.hudY - BLEED, L.W + BLEED * 2, L.hudH + BLEED).fill(COLORS.panel));
      const wave = label('', 16, COLORS.text, 'bold');
      wave.x = 12;
      wave.y = 12;
      const gold = label('', 16, COLORS.gold, 'bold');
      gold.anchor.set(1, 0);
      gold.x = L.W - 54;
      gold.y = 12;
      const menu = this.button('☰', 0x3a3f4b, () => {
        sound.play('tap');
        this.hooks.onMenu();
      }, 34, 30, 16);
      menu.x = L.W - 44;
      menu.y = 7;
      const speedBtn = this.button('', 0x3a3f4b, () => {
        sound.play('tap');
        this.game.cycleSpeed();
      }, 56, 30, 13);
      speedBtn.x = (L.W - 56) / 2 - 10;
      speedBtn.y = 7;
      const speedLabel = speedBtn.children[speedBtn.children.length - 1] as Text;
      const bar = new Graphics();
      this.hud.addChild(wave, gold, menu, speedBtn, bar);
      this.hudRefs = { wave, gold, speedBtn, speedLabel, bar };
    }
    const r = this.hudRefs;
    const waveText = s.endless ? `${this.t('wave')} ${s.wave} · ∞` : `${this.t('wave')} ${Math.min(s.wave, s.totalWaves)}/${s.totalWaves}`;
    const lvl = this.hooks.levelNumber !== null ? `${this.t('level_short', { n: this.hooks.levelNumber })} · ` : '';
    r.wave.text = s.modifiers.length > 0 ? `⚡ ${waveText}` : `${lvl}${waveText}`;
    r.wave.style.fontSize = lvl ? 14 : 16;
    r.gold.text = `${s.gold + (b ? b.gold : 0)} ◆`;
    r.speedBtn.visible = b !== null;
    r.speedLabel.text = this.t('speed', { n: this.game.speed });
    // Battle: how much of the whole wave is still standing. Placing: XP toward the next perk.
    r.bar.clear();
    if (b && b.waveHpTotal > 0) {
      const left = Math.max(0, 1 - b.waveHpDealt / b.waveHpTotal);
      r.bar.rect(0, L.hudH - 4, L.W, 4).fill(COLORS.hpBg);
      r.bar.rect(0, L.hudH - 4, L.W * left, 4).fill(COLORS.danger);
    } else {
      const need = xpCost(s, this.game.balance);
      const frac = need > 0 ? Math.min(1, s.xp / need) : 0;
      r.bar.rect(0, L.hudH - 4, L.W, 4).fill(COLORS.hpBg);
      r.bar.rect(0, L.hudH - 4, L.W * frac, 4).fill(COLORS.accent);
    }
  }

  private renderGrid(): void {
    this.grid.removeChildren();
    this.cellNodes.clear();
    const s = this.game.state;
    const placing = s.phase === 'placing';
    const routeCells = new Set<number>();
    for (const r of this.paths.routes) for (const i of r) routeCells.add(i);
    const breakCells = new Set(this.paths.breakCells);
    const spawnCols = new Set(this.paths.spawnColumns);
    const firstBuildRow = s.height - s.buildRows;
    let baseMinX = Infinity;
    let baseMinY = Infinity;
    let baseMaxX = -1;
    let baseMaxY = -1;

    for (let y = 0; y < s.height; y++) {
      for (let x = 0; x < s.width; x++) {
        const cell = { x, y };
        const idx = y * s.width + x;
        const cs = s.grid[idx]!;
        const r = this.cellRect(cell);
        const c = new Container();
        c.pivot.set(r.w / 2, r.h / 2);
        c.x = r.x + r.w / 2;
        c.y = r.y + r.h / 2;

        if (cs.kind === 'base') {
          baseMinX = Math.min(baseMinX, x);
          baseMinY = Math.min(baseMinY, y);
          baseMaxX = Math.max(baseMaxX, x);
          baseMaxY = Math.max(baseMaxY, y);
          this.grid.addChild(c);
          this.cellNodes.set(cellKey(cell), c);
          continue;
        }

        const tile = new Graphics();
        const onRoute = routeCells.has(idx) && cs.kind !== 'unit';
        const noBuild = y < firstBuildRow;
        // The lawn runs from a darker far edge to a lighter near edge, like the reference; a checker
        // whisper keeps neighbouring cells distinguishable for drag targets.
        const rowK = s.height > 1 ? y / (s.height - 1) : 1;
        const pal = this.palette();
        const grassBase = mixColor(mixColor(pal.grassFar, pal.grassNear, rowK), 0xffffff, (x + y) % 2 === 0 ? 0 : 0.035);
        const dirtBase = mixColor(pal.dirt, pal.dirtLight, rowK * 0.5);
        const tilePic = art.sprite(onRoute ? 'tile:dirt' : 'tile:grass', r.w, r.h);
        if (tilePic) {
          tilePic.x = r.w / 2;
          tilePic.y = r.h / 2;
          c.addChild(tilePic);
          if (noBuild) tile.roundRect(0, 0, r.w, r.h, 5).fill({ color: 0x1b2a1a, alpha: 0.22 });
        } else if (onRoute) drawDirtTile(tile, r.w, r.h, dirtBase, idx, noBuild);
        else drawGrassTile(tile, r.w, r.h, grassBase, idx, noBuild);
        if (y === 0 && spawnCols.has(x) && cs.kind !== 'unit') {
          tile.poly([r.w / 2 - 7, 5, r.w / 2 + 7, 5, r.w / 2, 13]).fill({ color: COLORS.danger, alpha: 0.7 });
        }
        c.addChild(tile);

        if (cs.kind === 'closed') {
          const rockPic = art.sprite('rock', r.w + 2, r.h - 2);
          if (rockPic) {
            rockPic.anchor.set(0.5, 1);
            rockPic.x = r.w / 2;
            rockPic.y = r.h - 3;
            c.addChild(rockPic);
          }
          const rock = new Graphics();
          if (!rockPic) drawRock(rock, r.w, r.h);
          if (cs.cost <= 0) rock.roundRect(0, 0, r.w, r.h, 6).fill({ color: 0x000000, alpha: 0.18 });
          c.addChild(rock);
          const clearable = cs.cost > 0 && canBuildAt(s, cell);
          if (clearable) {
            const selected = this.selectedCell !== null && this.selectedCell.x === x && this.selectedCell.y === y;
            if (selected) {
              // Ghosted: the road under it is already drawn as it would run once the rock is gone.
              if (rockPic) rockPic.alpha = 0.4;
              rock.alpha = 0.4;
              c.addChild(new Graphics().roundRect(0, 0, r.w, r.h, 8).stroke({ width: 3, color: 0xffffff }));
            }
            const cost = tileLabel(`${cs.cost} ◆`, 10);
            cost.style.fill = s.gold >= cs.cost ? COLORS.gold : COLORS.textDim;
            centerIn(cost, 0, r.h - 16, r.w, 14);
            c.addChild(cost);
            if (placing) {
              c.eventMode = 'static';
              c.cursor = 'pointer';
              // First tap shows what clearing does (road preview and the panel), the second tap pays.
              c.on('pointertap', () => {
                if (selected) this.clearRock(cell);
                else {
                  this.selectedCell = cell;
                  haptic.light();
                  this.renderAll();
                }
              });
            }
          }
        } else if (cs.kind === 'unit') {
          c.addChild(this.buildingTile(cs.unit, r.w, r.h));
          if (breakCells.has(idx)) {
            c.addChild(new Graphics().roundRect(1, 1, r.w - 2, r.h - 2, 8).stroke({ width: 3, color: COLORS.danger, alpha: 0.9 }));
          }
          if (this.selectedCell && this.selectedCell.x === x && this.selectedCell.y === y) {
            c.addChild(new Graphics().roundRect(0, 0, r.w, r.h, 8).stroke({ width: 3, color: 0xffffff }));
          }
          if (placing) {
            c.eventMode = 'static';
            c.cursor = 'grab';
            c.on('pointerdown', (e) => this.beginDrag({ kind: 'cell', cell }, cs.unit, e));
          }
        }
        this.grid.addChild(c);
        this.cellNodes.set(cellKey(cell), c);
      }
    }

    // Build line: everything above it belongs to the enemy.
    if (firstBuildRow > 0 && firstBuildRow < s.height) {
      const zy = L.gridY + firstBuildRow * (this.cellPx() + L.gap) - L.gap / 2 - 0.5;
      const line = new Graphics();
      const x0 = this.originX() - 6;
      const x1 = this.originX() + this.boardWidth() + 6;
      for (let x = x0; x < x1; x += 12) {
        line.moveTo(x, zy).lineTo(Math.min(x + 7, x1), zy).stroke({ width: 3, color: 0xfff3c4, alpha: 0.9 });
      }
      const tag = tileLabel(this.t('build_zone'), 8.5);
      tag.anchor.set(1, 1);
      tag.x = x1 - 2;
      tag.y = zy - 2;
      line.eventMode = 'none';
      tag.eventMode = 'none';
      this.grid.addChild(line, tag);
    }

    // The bunker sits on its own cells and shows its HP.
    if (baseMaxX >= 0) {
      const r0 = this.cellRect({ x: baseMinX, y: baseMinY });
      const r1 = this.cellRect({ x: baseMaxX, y: baseMaxY });
      const w = r1.x + r1.w - r0.x;
      const h = r1.y + r1.h - r0.y;
      const bunker = new Container();
      bunker.eventMode = 'none';
      bunker.x = r0.x;
      bunker.y = r0.y;
      this.bunkerRect = { x: r0.x, y: r0.y, w, h };
      const g = new Graphics();
      g.roundRect(0, 0, w, h, 8).fill(COLORS.dirt).stroke({ width: 1, color: COLORS.dirtLine });
      // The bitmap bunker is a cube, taller than the 2×1 plate: it stands on the plate and rises past the cell top.
      const bunkerPic = art.sprite('bunker', w - 8, h * 1.55);
      if (!bunkerPic) drawBunker(g, w, h);
      bunker.addChild(g);
      if (bunkerPic) {
        bunkerPic.anchor.set(0.5, 1);
        bunkerPic.x = w / 2;
        bunkerPic.y = h - 9;
        bunker.addChild(bunkerPic);
      }
      const hp = this.game.battle ? this.game.battle.wallHp : s.wallHp;
      const max = this.game.battle ? this.game.battle.wallMax : s.wallMax;
      const bar = new Graphics();
      bar.roundRect(6, h - 10, w - 12, 6, 3).fill(COLORS.hpBg).stroke({ width: 1, color: INK });
      bar.roundRect(6, h - 10, ((w - 12) * Math.max(0, hp)) / Math.max(1, max), 6, 3).fill(hp / max > 0.3 ? COLORS.hp : COLORS.danger);
      bunker.addChild(bar);
      if (!bunkerPic) {
        const name = tileLabel(this.theme.wall.name[this.locale], 9);
        centerIn(name, 0, 2, w, 14);
        bunker.addChild(name);
      }
      this.grid.addChild(bunker);
      this.bunkerNode = bunker;
    } else {
      this.bunkerNode = null;
      this.bunkerRect = null;
    }
  }

  private collectBattleEffects(): void {
    const b = this.game.battle;
    if (!b) return;
    const s = this.game.state;
    const heroColor = hexToNum(this.theme.heroes[s.heroId].color);

    // Tracers. Damage numbers come from the hits below, so splash and chain count every enemy they touched.
    for (const sh of b.lastShots) {
      const from = this.fieldPos(sh.fromX, sh.fromY);
      const to = this.fieldPos(sh.toX, sh.toY);
      const color =
        sh.kind === 'hero' ? heroColor : sh.kind === 'soldier' ? hexToNum(this.theme.soldiers[sh.arch]?.color ?? '#8ab4ff') : hexToNum(this.theme.units[sh.arch].color);
      this.flashes.push({
        fx: from.x,
        fy: from.y,
        tx: to.x,
        ty: to.y,
        color,
        life: 0,
        ttl: sh.splashRadius > 0 ? 260 : sh.kind === 'hero' ? 150 : 110,
        splashPx: sh.splashRadius * this.ppm(),
      });
      const sfx = sh.kind === 'hero' ? 'shot_sniper' : sh.kind === 'soldier' ? 'shot_rifle' : SHOT_SFX[sh.arch];
      if (sfx) sound.play(sfx, { gain: this.game.speed > 1 ? 0.7 : 1 });
      if (sh.kind === 'unit') {
        // Recoil on the building and a muzzle puff; the MG also spits a casing.
        const cell = this.cellAtPoint(from.x, from.y);
        if (cell) this.pop(cell, sh.arch === 'splash' ? 1.1 : 1.05);
        if (this.particles.length < 240) {
          this.particles.push({ x: from.x + (to.x - from.x) * 0.12, y: from.y - 8 + (to.y - from.y) * 0.12, vx: (to.x - from.x) * 0.3, vy: (to.y - from.y) * 0.3, life: 0, ttl: 140, color: 0xfff1c4, size: 3, gravity: 0, grow: 1.8, alpha: 0.7 });
          if (sh.arch === 'rapid') this.particles.push({ x: from.x + 6, y: from.y - 6, vx: 30 + Math.random() * 20, vy: -60, life: 0, ttl: 420, color: 0xe0b34a, size: 1.4, gravity: 420, grow: 0.8 });
        }
      }
    }

    // Blows: one number per enemy per tick, a white flash on the victim, red flashes on our side.
    const perEnemy = new Map<number, { x: number; y: number; amount: number; source: HitSource }>();
    let wallDmg = 0;
    let structureHit = false;
    for (const h of b.lastHits) {
      if (h.target === 'enemy') {
        this.enemyFlash.set(h.id, 120);
        const cur = perEnemy.get(h.id);
        if (cur) {
          cur.amount += h.amount;
          if (h.source === 'burn' || h.source === 'crit') cur.source = h.source;
        } else perEnemy.set(h.id, { x: h.x, y: h.y, amount: h.amount, source: h.source });
        if (h.source === 'melee') sound.play('melee', { gain: 0.6 });
        else if (h.source === 'crit') {
          sound.play('crit');
          const p = this.fieldPos(h.x, h.y);
          this.burst(p.x, p.y, heroColor, 8);
        }
      } else if (h.target === 'structure') {
        this.cellFlash.set(h.index, 200);
        structureHit = true;
      } else if (h.target === 'wall') {
        wallDmg += h.amount;
      } else if (h.target === 'soldier') {
        if (h.hero) this.heroFlash = 140;
        const p = this.fieldPos(h.x, h.y);
        if (this.floaters.length < 30) this.floatNumber(`-${h.amount}`, p.x, p.y - 12, h.hero ? heroColor : COLORS.danger, 10);
      }
    }
    for (const hit of perEnemy.values()) {
      if (this.floaters.length >= 30) break;
      const p = this.fieldPos(hit.x, hit.y);
      const color = hit.source === 'burn' ? 0xff9f43 : hit.source === 'crit' ? COLORS.gold : hit.source === 'melee' ? heroColor : 0xffffff;
      const size = hit.source === 'crit' ? 17 : hit.amount >= 40 ? 14 : hit.amount >= 15 ? 12 : 11;
      this.floatNumber(hit.source === 'crit' ? `${hit.amount}!` : String(hit.amount), p.x, p.y - 12, color, size);
    }
    if (structureHit) {
      sound.play('structure_hit', { gain: 0.6 });
      this.startShake(1.5, 120);
    }
    if (wallDmg > 0) {
      this.bunkerFlash = 240;
      this.startShake(3, 220);
      sound.play('wall_hit');
      haptic.light();
      const r = this.bunkerRect;
      if (r) this.floatNumber(`-${wallDmg}`, r.x + r.w / 2, r.y - 4, COLORS.danger, 15);
    }

    // Deaths: a burst in the enemy's colour and the gold it paid.
    for (const d of b.lastDeaths) {
      const p = this.fieldPos(d.x, d.y);
      const color = hexToNum(this.theme.enemies[d.arch].color);
      this.burst(p.x, p.y, color, d.arch === 'boss' ? 30 : d.arch === 'brute' ? 14 : 8);
      if (d.bounty > 0 && this.floaters.length < 32) this.floatNumber(`+${d.bounty} ◆`, p.x + 8, p.y - 22, COLORS.gold, 11);
      if (d.arch === 'boss') {
        sound.play('boss_die');
        this.startShake(7, 550);
        this.banner(this.t('banner_boss_down'), COLORS.gold, undefined, COLORS.gold, 1300);
        haptic.success();
      } else sound.play('enemy_die', { pitch: DIE_PITCH[d.arch], gain: 0.7 });
    }
    for (const sd of b.lastSoldierDeaths) {
      const p = this.fieldPos(sd.x, sd.y);
      this.burst(p.x, p.y, sd.hero ? heroColor : 0xbfc7d5, sd.hero ? 18 : 6);
      if (sd.hero) {
        sound.play('hero_down');
        this.floatNumber(this.t('hero_down'), p.x, p.y - 18, heroColor, 12);
      }
    }
    for (const sp of b.lastSoldierSpawns) {
      const p = this.fieldPos(sp.x, sp.y);
      this.flashes.push({ fx: p.x, fy: p.y, tx: p.x, ty: p.y, color: sp.hero ? heroColor : 0xffffff, life: 0, ttl: 220, splashPx: sp.hero ? 22 : 12 });
      sound.play('soldier_spawn', { pitch: sp.hero ? 0.8 : 1, gain: 0.5 });
    }
    for (const loss of b.lastStructureLosses) {
      const r = this.cellRect({ x: loss.index % b.W, y: Math.floor(loss.index / b.W) });
      this.burst(r.x + r.w / 2, r.y + r.h / 2, hexToNum(this.theme.units[loss.arch].color), 18);
      sound.play('structure_break');
      this.startShake(4, 300);
    }
    for (const bu of b.lastBursts) {
      const p = this.fieldPos(bu.x, bu.y);
      this.flashes.push({ fx: p.x, fy: p.y, tx: p.x, ty: p.y, color: heroColor, life: 0, ttl: 320, splashPx: bu.radius * this.ppm() });
      sound.play('hero_skill', { gain: 0.8 });
    }
    for (const ab of b.lastAbilities) {
      const p = this.fieldPos(ab.x, ab.y);
      const color = ab.ability === 'summon' ? 0xc56cf0 : ab.ability === 'smash' ? 0xff7043 : 0x2ecc71;
      if (ab.ability === 'summon') {
        this.burst(p.x, p.y, color, 14);
        this.flashes.push({ fx: p.x, fy: p.y, tx: p.x, ty: p.y, color, life: 0, ttl: 360, splashPx: 30 });
        sound.play('boss_intro', { gain: 0.5, pitch: 1.4 });
      } else if (ab.ability === 'smash') {
        this.startShake(3, 200);
        sound.play('structure_hit');
      } else if (this.floaters.length < 32) {
        this.floatNumber('+', p.x, p.y - 26, color, 14);
      }
    }

    // First sight of each enemy: the boss gets a roar and a jolt.
    for (const e of b.enemies) {
      if (this.knownEnemies.has(e.id)) continue;
      this.knownEnemies.add(e.id);
      if (e.arch === 'boss') {
        sound.play('boss_intro');
        this.startShake(5, 450);
        haptic.medium();
      }
    }

    // Bunker below a third: a slow heartbeat until it recovers or falls.
    if (b.wallHp > 0 && b.wallHp * 10 < b.wallMax * 3) {
      if (this.now - this.lowHpBeat > 1300) {
        this.lowHpBeat = this.now;
        sound.play('wall_low');
      }
    }
  }

  private burst(x: number, y: number, color: number, n: number): void {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + Math.random() * 0.4;
      const sp = 60 + Math.random() * 90;
      this.particles.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 40, life: 0, ttl: 350 + Math.random() * 250, color, size: 2 + Math.random() * 2.5 });
    }
  }

  private floatNumber(n: string, x: number, y: number, color: number, size = 11): void {
    const text = this.floaterPool.pop() ?? tileLabel('', 11);
    text.text = n;
    text.style.fill = color;
    text.style.fontSize = size;
    text.anchor.set(0.5);
    text.x = x + (Math.random() - 0.5) * 10;
    text.y = y;
    text.alpha = 1;
    this.floaterLayer.addChild(text);
    this.floaters.push({ text, life: 0, ttl: 650 });
  }

  /** Get or create the bitmap body for a field actor; `used` marks it alive this frame. */
  private actorSprite(id: string, key: string, size: number, used: Set<string>): Sprite | null {
    if (!art.has(key)) return null;
    let sp = this.fieldSpriteMap.get(id);
    if (!sp) {
      sp = art.sprite(key, size, size)!;
      this.fieldSpriteMap.set(id, sp);
      this.fieldSprites.addChild(sp);
      this.spriteBase.set(id, sp.scale.x);
      this.spriteBorn.set(id, this.now);
    }
    used.add(id);
    return sp;
  }

  /**
   * Frame-by-frame actor when the pack has a walk cycle for `name`; otherwise null and the caller
   * falls back to the single sprite. `height` is the on-screen height of the tallest frame.
   */
  private animatedSprite(id: string, name: string, height: number, used: Set<string>): { sp: Sprite; set: import('./art').FrameSet } | null {
    const set = art.frames(name);
    if (!set) return null;
    let sp = this.fieldSpriteMap.get(id);
    if (!sp) {
      sp = new Sprite(set.walk[0]!);
      sp.anchor.set(0.5, 0.96);
      const k = height / set.refH;
      sp.scale.set(k);
      this.fieldSpriteMap.set(id, sp);
      this.fieldSprites.addChild(sp);
      this.spriteBase.set(id, k);
      this.spriteBorn.set(id, this.now);
      this.spriteAnimated.add(id);
    }
    used.add(id);
    return { sp, set };
  }

  /**
   * Pick the frame for this moment. Fighting: the wind-up frame from half the swing, the strike frame held
   * for 140 ms after the blow lands (the sim resets the swing to zero at that instant). Otherwise the walk
   * cycle by time, offset per actor so a crowd does not march in lockstep.
   */
  private pickFrame(id: string, set: import('./art').FrameSet, fighting: boolean, swing: number, rateMs: number, seed: number): Texture {
    const prev = this.prevSwing.get(id) ?? 0;
    if (prev > 0.5 && swing < prev) this.strikeUntil.set(id, this.now + 140);
    this.prevSwing.set(id, swing);
    if (set.attack.length > 0) {
      if (this.now < (this.strikeUntil.get(id) ?? 0)) return set.attack[set.attack.length - 1]!;
      if (fighting && swing >= 0.5) return set.attack[0]!;
    }
    const n = set.walk.length;
    const idx = Math.floor((this.now + seed * 137) / rateMs) % n;
    return set.walk[idx]!;
  }

  /** Squash-and-stretch on top of real frames: a hit squash and a spawn pop, plus a walk lean when the cycle is subtle. */
  private frameScale(id: string, sp: Sprite, flash: number | undefined, lean: number): void {
    const base = this.spriteBase.get(id) ?? sp.scale.x;
    let sx = base * (1 + 0.03 * Math.abs(lean));
    let sy = base * (1 - 0.03 * Math.abs(lean));
    sp.rotation = 0.05 * lean;
    if (flash !== undefined) {
      const k = Math.min(1, flash / 120);
      sx *= 1 + 0.15 * k;
      sy *= 1 - 0.15 * k;
    }
    const age = this.now - (this.spriteBorn.get(id) ?? this.now);
    if (age < 220) {
      const pop = age / 220;
      const ease = 1 - (1 - pop) * (1 - pop);
      sx *= 0.4 + 0.6 * ease + 0.15 * Math.sin(pop * Math.PI);
      sy *= 0.4 + 0.6 * ease + 0.15 * Math.sin(pop * Math.PI);
    }
    sp.scale.set(sx, sy);
  }

  /** Walk lean, a wind-up squash before a blow, and a pop on arrival for soldiers and the hero. */
  private animateActor(sp: Sprite, id: string, swing: number, seed: number): void {
    const base = this.spriteBase.get(id) ?? sp.scale.x;
    const stride = Math.sin(this.now / 90 + seed * 1.7);
    let sx = base * (1 + 0.04 * Math.abs(stride));
    let sy = base * (1 - 0.04 * Math.abs(stride));
    sp.rotation = 0.06 * stride;
    if (swing > 0.6) {
      const k = (swing - 0.6) / 0.4;
      sx *= 1 - 0.1 * k;
      sy *= 1 + 0.12 * k;
    }
    const age = this.now - (this.spriteBorn.get(id) ?? this.now);
    if (age < 220) {
      const pop = age / 220;
      const ease = 1 - (1 - pop) * (1 - pop);
      sx *= 0.4 + 0.6 * ease + 0.15 * Math.sin(pop * Math.PI);
      sy *= 0.4 + 0.6 * ease + 0.15 * Math.sin(pop * Math.PI);
    }
    sp.scale.set(sx, sy);
  }

  private renderField(): void {
    const g = this.fieldFx;
    g.clear();
    const s = this.game.state;
    const b = this.game.battle;
    const usedSprites = new Set<string>();

    // Range of the selected building while placing.
    const sel = this.selectedCell;
    if (!b && sel) {
      const cs = s.grid[sel.y * s.width + sel.x];
      if (cs && cs.kind === 'unit') {
        const st = effectiveStats(cs.unit, this.game.balance, computeMods(s.perks), s.cardLevels[cs.unit.arch], s.skills);
        if (st.range > 0) {
          const c = this.fieldPos(sel.x * 1000 + 500, sel.y * 1000 + 500);
          const color = hexToNum(this.theme.units[cs.unit.arch].color);
          g.circle(c.x, c.y, st.range * this.ppm()).fill({ color, alpha: 0.1 }).stroke({ width: 2, color, alpha: 0.7 });
        }
      }
    }

    // Buildings being chewed on flash red; the bunker too.
    for (const [idx, ms] of this.cellFlash) {
      const r = this.cellRect({ x: idx % s.width, y: Math.floor(idx / s.width) });
      g.roundRect(r.x, r.y, r.w, r.h, 6).fill({ color: COLORS.danger, alpha: 0.45 * (ms / 200) });
    }
    if (this.bunkerFlash > 0 && this.bunkerRect) {
      const r = this.bunkerRect;
      g.roundRect(r.x, r.y, r.w, r.h, 8).fill({ color: COLORS.danger, alpha: 0.5 * (this.bunkerFlash / 240) });
    }

    if (b) {
      // Structure HP bars (only when damaged), then cracks, fire and smoke by damage stage.
      for (const st of b.structures.values()) {
        if (!st.alive || st.hp >= st.maxHp) continue;
        const r = this.cellRect({ x: st.index % b.W, y: Math.floor(st.index / b.W) });
        g.roundRect(r.x + 4, r.y + r.h - 6, r.w - 8, 4, 2).fill(COLORS.hpBg);
        g.roundRect(r.x + 4, r.y + r.h - 6, ((r.w - 8) * st.hp) / st.maxHp, 4, 2).fill(COLORS.danger);
      }
      this.damageStages(g, 16);
      // Live status over working buildings: spawn timer and alive pips for camps, reload for guns, repair pulse.
      const aliveByOwner = new Map<number, number>();
      for (const so of b.soldiers) if (so.alive && !so.hero) aliveByOwner.set(so.owner, (aliveByOwner.get(so.owner) ?? 0) + 1);
      for (const st of b.structures.values()) {
        if (!st.alive) continue;
        const t = st.stats;
        const r = this.cellRect({ x: st.index % b.W, y: Math.floor(st.index / b.W) });
        const bx = r.x + 4;
        const by = r.y + 3;
        const bw = r.w - 8;
        if (t.soldier && t.spawnInterval > 0) {
          const alive = aliveByOwner.get(st.unit.id) ?? 0;
          const color = hexToNum(this.theme.soldiers[st.unit.arch]?.color ?? '#8ab4ff');
          if (alive < t.spawnMax) {
            g.roundRect(bx, by, bw, 3, 1.5).fill({ color: COLORS.hpBg, alpha: 0.9 });
            g.roundRect(bx, by, (bw * Math.min(st.spawnAcc, t.spawnInterval)) / t.spawnInterval, 3, 1.5).fill(color);
          }
          for (let i = 0; i < t.spawnMax; i++) {
            g.circle(bx + 3 + i * 7, by + 8, 2.4).fill({ color: i < alive ? color : COLORS.hpBg, alpha: i < alive ? 1 : 0.8 }).stroke({ width: 1, color: INK, alpha: 0.6 });
          }
        } else if (t.cooldown > 0 && st.cd > 0) {
          const color = hexToNum(this.theme.units[st.unit.arch].color);
          g.roundRect(bx, by, bw, 2.5, 1.2).fill({ color: COLORS.hpBg, alpha: 0.7 });
          g.roundRect(bx, by, (bw * (t.cooldown - st.cd)) / t.cooldown, 2.5, 1.2).fill({ color, alpha: 0.85 });
        } else if (t.heal > 0 && t.healInterval > 0) {
          g.roundRect(bx, by, bw, 3, 1.5).fill({ color: COLORS.hpBg, alpha: 0.8 });
          g.roundRect(bx, by, (bw * st.healAcc) / t.healInterval, 3, 1.5).fill({ color: COLORS.hp, alpha: 0.9 });
        }
      }
      const melee2 = this.game.balance.enemyMeleeRange * this.game.balance.enemyMeleeRange;
      for (const so of b.soldiers) {
        if (!so.alive) continue;
        const p = this.fieldPos(so.x, so.y);
        // Marching bob; a wind-up toward the target right before each blow.
        p.y += Math.sin(this.now / 95 + so.id * 1.7) * 1.2;
        if (so.interval > 0 && so.acc > 0) {
          const k = so.acc / so.interval;
          const lunge = k > 0.6 ? ((k - 0.6) / 0.4) * 4 : 0;
          p.y -= lunge;
        }
        if (so.hero) {
          let color = hexToNum(this.theme.heroes[s.heroId].color);
          if (this.heroFlash > 0) color = mix(color, 0xffffff, Math.min(1, this.heroFlash / 140) * 0.8);
          const hAnim = this.animatedSprite(`s${so.id}`, `hero:${s.heroId}`, Math.round(44 * this.cellK()), usedSprites);
          const hp = hAnim ? hAnim.sp : this.actorSprite(`s${so.id}`, `hero:${s.heroId}`, Math.round(38 * this.cellK()), usedSprites);
          const hSwing = so.acc > 0 && so.interval > 0 ? so.acc / so.interval : 0;
          if (hp && hAnim) {
            hp.texture = this.pickFrame(`s${so.id}`, hAnim.set, hSwing > 0, hSwing, 110, so.id);
            hp.position.set(p.x, p.y + 8);
            hp.alpha = this.heroFlash > 0 ? 0.6 + 0.4 * (1 - this.heroFlash / 140) : 1;
            this.frameScale(`s${so.id}`, hp, this.heroFlash > 0 ? this.heroFlash : undefined, hSwing > 0 ? 0 : Math.sin(this.now / 90 + so.id * 1.7));
          } else if (hp) {
            hp.anchor.set(0.5, 0.78);
            hp.position.set(p.x, p.y + 6);
            hp.alpha = this.heroFlash > 0 ? 0.6 + 0.4 * (1 - this.heroFlash / 140) : 1;
            this.animateActor(hp, `s${so.id}`, hSwing, so.id);
          } else drawHero(g, p.x, p.y, color, s.heroId);
          g.rect(p.x - 11, p.y - 17, 22, 3).fill(COLORS.hpBg);
          g.rect(p.x - 11, p.y - 17, (22 * so.hp) / so.maxHp, 3).fill(COLORS.hp);
          continue;
        }
        const ownerArch = this.soldierArch(so.owner);
        const color = hexToNum(this.theme.soldiers[ownerArch]?.color ?? '#8ab4ff');
        const sAnim = this.animatedSprite(`s${so.id}`, `soldier:${ownerArch}`, Math.round(30 * this.cellK()), usedSprites);
        const ssp = sAnim ? sAnim.sp : this.actorSprite(`s${so.id}`, `soldier:${ownerArch}`, Math.round(26 * this.cellK()), usedSprites);
        const swing = so.acc > 0 && so.interval > 0 ? so.acc / so.interval : 0;
        if (ssp && sAnim) {
          ssp.texture = this.pickFrame(`s${so.id}`, sAnim.set, swing > 0, swing, ownerArch === 'stable' ? 80 : 110, so.id);
          ssp.position.set(p.x, p.y + 6);
          this.frameScale(`s${so.id}`, ssp, undefined, swing > 0 ? 0 : Math.sin(this.now / 90 + so.id * 1.7));
        } else if (ssp) {
          ssp.anchor.set(0.5, 0.78);
          ssp.position.set(p.x, p.y + 4);
          this.animateActor(ssp, `s${so.id}`, swing, so.id);
        }
        else drawSoldier(g, ownerArch, p.x, p.y, color);
        if (so.hp < so.maxHp) {
          g.rect(p.x - 7, p.y - 12, 14, 2.5).fill(COLORS.hpBg);
          g.rect(p.x - 7, p.y - 12, (14 * so.hp) / so.maxHp, 2.5).fill(COLORS.hp);
        }
      }
      for (const e of b.enemies) {
        if (!e.alive) continue;
        const p = this.fieldPos(e.x, e.y);
        const th0 = this.theme.enemies[e.arch];
        const th = { ...th0, radius: Math.max(4, Math.round(th0.radius * this.cellK())) };
        // Where is this one swinging? At a building, at the bunker, or at a soldier next to it.
        let lx = 0;
        let ly = 0;
        let fighting = false;
        if (e.blocked >= 0) {
          const st = b.structures.get(e.blocked);
          if (st && st.alive) {
            const c = this.fieldPos(st.x, st.y);
            lx = c.x - p.x;
            ly = c.y - p.y;
            fighting = true;
          }
        } else if (e.atBase) {
          ly = 1;
          fighting = true;
        } else {
          for (const so of b.soldiers) {
            if (!so.alive) continue;
            const dx = so.x - e.x;
            const dy = so.y - e.y;
            if (dx * dx + dy * dy <= melee2) {
              const c = this.fieldPos(so.x, so.y);
              lx = c.x - p.x;
              ly = c.y - p.y;
              fighting = true;
              break;
            }
          }
        }
        if (fighting && e.interval > 0) {
          const len = Math.hypot(lx, ly) || 1;
          const k = e.acc / e.interval;
          const lunge = k > 0.55 ? ((k - 0.55) / 0.45) * 6 : 0;
          p.x += (lx / len) * lunge;
          p.y += (ly / len) * lunge;
        } else {
          // Walking: a bob keyed to the enemy id so the horde does not move in lockstep.
          const rate = e.arch === 'runner' ? 60 : e.arch === 'brute' || e.arch === 'boss' ? 160 : 100;
          p.y += Math.sin(this.now / rate + e.id * 2.1) * (e.arch === 'boss' ? 2 : 1.4);
        }
        let color = hexToNum(th.color);
        const fl = this.enemyFlash.get(e.id);
        if (fl !== undefined) color = mix(color, 0xffffff, Math.min(1, fl / 120) * 0.85);
        if (e.arch === 'boss') {
          const auraColor = e.ability === 'summon' ? 0xc56cf0 : e.ability === 'smash' ? 0xff7043 : e.ability === 'regen' ? 0x2ecc71 : COLORS.danger;
          g.circle(p.x, p.y + 2, th.radius + 6 + Math.sin(this.now / 120) * 2).fill({ color: auraColor, alpha: e.ability ? 0.28 : 0.18 });
          if (e.ability) g.circle(p.x, p.y + 2, th.radius + 10 + Math.sin(this.now / 120) * 2).stroke({ width: 2, color: auraColor, alpha: 0.5 });
        }
        let wing = 0;
        if (e.flying) {
          // Airborne: a shadow on the ground, the body lifted, wings flapping.
          g.ellipse(p.x, p.y + 9, th.radius * 0.9, th.radius * 0.35).fill({ color: 0x000000, alpha: 0.22 });
          p.y -= 7;
          wing = Math.sin(this.now / 45 + e.id);
        }
        // Chibi bodies are drawn standing on the point, so anchor them at the feet and let them stand taller than the hit circle.
        const animated = this.animatedSprite(`e${e.id}`, e.arch, th.radius * 3.6, usedSprites);
        const esp = animated ? animated.sp : this.actorSprite(`e${e.id}`, `enemy:${e.arch}`, th.radius * 3.4, usedSprites);
        if (esp && animated) {
          // Real frames for the attack; the walk frames on most sheets barely differ, so a light lean rides on top.
          const rate = e.arch === 'runner' ? 70 : e.arch === 'flyer' ? 80 : e.arch === 'brute' || e.arch === 'boss' ? 170 : 120;
          esp.texture = this.pickFrame(`e${e.id}`, animated.set, fighting, e.interval > 0 ? e.acc / e.interval : 0, rate, e.id);
          esp.position.set(p.x, p.y + th.radius * (e.flying ? 0.5 : 0.9));
          const lean = fighting || e.flying ? 0 : Math.sin(this.now / rate + e.id * 2.1);
          this.frameScale(`e${e.id}`, esp, fl, lean);
          esp.alpha = fl !== undefined ? 0.55 + 0.45 * (1 - Math.min(1, fl / 120)) : 1;
          if (fl !== undefined) g.circle(p.x, p.y, th.radius * 1.1).fill({ color: 0xffffff, alpha: 0.35 * Math.min(1, fl / 120) });
          if (!e.flying && !fighting && this.particles.length < 240 && Math.random() < 0.06) {
            this.particles.push({ x: p.x + (Math.random() - 0.5) * 8, y: p.y + th.radius * 0.7, vx: (Math.random() - 0.5) * 14, vy: -6, life: 0, ttl: 380, color: 0xd8c39a, size: 1.6, gravity: -2, grow: 2, alpha: 0.5 });
          }
        } else if (esp) {
          esp.anchor.set(0.5, 0.78);
          esp.position.set(p.x, p.y + th.radius * 0.6);
          // One frame, made to move: a walk cycle as a lean and a stride squash, a hit as a squash-and-stretch,
          // a fresh spawn as a pop from small.
          const base = this.spriteBase.get(`e${e.id}`) ?? esp.scale.x;
          if (!this.spriteBase.has(`e${e.id}`)) this.spriteBase.set(`e${e.id}`, base);
          const rate = e.arch === 'runner' ? 55 : e.arch === 'brute' || e.arch === 'boss' ? 150 : 95;
          const stride = Math.sin(this.now / rate + e.id * 2.1);
          let sx = base * (1 + (fighting ? 0 : 0.05 * Math.abs(stride)));
          let sy = base * (1 - (fighting ? 0 : 0.05 * Math.abs(stride)));
          esp.rotation = fighting ? 0.12 * Math.sin(this.now / (rate * 0.7) + e.id) : 0.07 * stride;
          if (fl !== undefined) {
            const k = Math.min(1, fl / 120);
            sx *= 1 + 0.18 * k;
            sy *= 1 - 0.18 * k;
          }
          const age = this.now - (this.spriteBorn.get(`e${e.id}`) ?? this.now);
          if (age < 220) {
            const pop = age / 220;
            const ease = 1 - (1 - pop) * (1 - pop);
            sx *= 0.4 + 0.6 * ease + 0.15 * Math.sin(pop * Math.PI);
            sy *= 0.4 + 0.6 * ease + 0.15 * Math.sin(pop * Math.PI);
          }
          esp.scale.set(sx, sy);
          // A hit reads as a white flash: fade the sprite toward white by lowering alpha over a white disc.
          esp.alpha = fl !== undefined ? 0.55 + 0.45 * (1 - Math.min(1, fl / 120)) : 1;
          if (fl !== undefined) g.circle(p.x, p.y, th.radius * 1.1).fill({ color: 0xffffff, alpha: 0.35 * Math.min(1, fl / 120) });
          if (e.flying) esp.y += Math.sin(this.now / 45 + e.id) * 1.5;
          // Dust under walking feet on the ground.
          if (!e.flying && !fighting && this.particles.length < 240 && Math.random() < 0.06) {
            this.particles.push({ x: p.x + (Math.random() - 0.5) * 8, y: p.y + th.radius * 0.7, vx: (Math.random() - 0.5) * 14, vy: -6, life: 0, ttl: 380, color: 0xd8c39a, size: 1.6, gravity: -2, grow: 2, alpha: 0.5 });
          }
        } else drawEnemy(g, e.arch, p.x, p.y, th.radius, color, wing);
        if (e.burnTicks > 0) g.circle(p.x, p.y, th.radius + 3).stroke({ width: 2, color: 0xff9f43, alpha: 0.6 + Math.sin(this.now / 70) * 0.3 });
        const hpW = th.radius * 2;
        g.rect(p.x - th.radius, p.y - th.radius - 7, hpW, 3).fill(COLORS.hpBg);
        g.rect(p.x - th.radius, p.y - th.radius - 7, (hpW * e.hp) / e.maxHp, 3).fill(COLORS.danger);
      }

      // Actors that died or left take their sprites with them.
      for (const [id, sp] of this.fieldSpriteMap) {
        if (usedSprites.has(id)) continue;
        this.fieldSprites.removeChild(sp);
        sp.destroy();
        this.fieldSpriteMap.delete(id);
        this.spriteBase.delete(id);
        this.spriteBorn.delete(id);
        this.spriteAnimated.delete(id);
        this.prevSwing.delete(id);
        this.strikeUntil.delete(id);
      }

      // Bunker below a third: the board edges pulse red.
      if (b.wallHp > 0 && b.wallHp * 10 < b.wallMax * 3) {
        const a = 0.22 + Math.sin(this.now / 160) * 0.14;
        g.roundRect(this.originX() - 3, L.gridY - 3, this.boardWidth() + 6, this.boardHeight() + 6, 10).stroke({ width: 14, color: COLORS.danger, alpha: a });
      }
    }

    for (const f of this.flashes) {
      const k = 1 - f.life / f.ttl;
      if (f.splashPx > 0) {
        g.circle(f.tx, f.ty, f.splashPx * (1.2 - k * 0.4)).stroke({ width: 2, color: f.color, alpha: k * 0.8 });
        g.circle(f.tx, f.ty, f.splashPx * 0.5).fill({ color: f.color, alpha: k * 0.25 });
      }
      if (f.life < 120) {
        g.moveTo(f.fx, f.fy).lineTo(f.tx, f.ty).stroke({ width: 2, color: f.color, alpha: 0.9 * (1 - f.life / 120) });
        g.circle(f.tx, f.ty, 4).fill({ color: 0xffffff, alpha: 0.8 * (1 - f.life / 120) });
      }
    }
    for (const p of this.particles) {
      const k = p.life / p.ttl;
      const size = p.size * (1 + ((p.grow ?? 1) - 1) * k);
      g.circle(p.x, p.y, size).fill({ color: p.color, alpha: (p.alpha ?? 1) * (1 - k) });
    }
  }

  // ---------- ambient life on the board: chimneys, sparks, cracks, level growth ----------

  /** Where a building's smoke or sparks come out, relative to the cell's top-left. */
  private ventOf(arch: ArchetypeId, w: number, h: number): { x: number; y: number } | null {
    switch (arch) {
      case 'burn':
        return { x: w * 0.5, y: h * 0.16 };
      case 'mine':
        return { x: w * 0.62, y: h * 0.2 };
      case 'chain':
        return { x: w * 0.5, y: h * 0.14 };
      case 'repair':
        return { x: w * 0.7, y: h * 0.2 };
      case 'stable':
        return { x: w * 0.3, y: h * 0.4 };
      default:
        return null;
    }
  }

  /** Idle effects while placing and in battle: smoke from the flamethrower, depot and garage, tesla sparks. */
  private ambient(deltaMs: number): void {
    const s = this.game.state;
    if (s.phase !== 'placing' && s.phase !== 'battle' && s.phase !== 'perk') return;
    this.ambientAcc += deltaMs;
    if (this.ambientAcc < 90) return;
    this.ambientAcc = 0;
    if (this.particles.length > 220) return;
    for (const { cell, unit } of units(s)) {
      const vent = this.ventOf(unit.arch, this.cellPx(), this.cellPx());
      if (!vent) continue;
      const r = this.cellRect(cell);
      const x = r.x + vent.x;
      const y = r.y + vent.y;
      // Chance per 90 ms tick, so puffs are irregular; higher levels puff more.
      const lvl = Math.min(4, unit.level);
      if (unit.arch === 'chain') {
        if (Math.random() < 0.18 + lvl * 0.05) {
          const a = Math.random() * Math.PI * 2;
          this.particles.push({ x, y, vx: Math.cos(a) * 40, vy: Math.sin(a) * 40 - 20, life: 0, ttl: 160 + Math.random() * 120, color: 0xd6a5ff, size: 1.5 + Math.random() * 1.5, gravity: 0, grow: 0.3 });
        }
        continue;
      }
      const chance = unit.arch === 'burn' ? 0.5 : unit.arch === 'stable' ? 0.2 : 0.3;
      if (Math.random() < chance + lvl * 0.04) {
        const dark = unit.arch === 'burn' ? 0x5b5b66 : unit.arch === 'stable' ? 0x9aa0a8 : 0xc9c9cf;
        this.particles.push({ x: x + (Math.random() - 0.5) * 4, y, vx: (Math.random() - 0.5) * 8 + 4, vy: -14 - Math.random() * 10, life: 0, ttl: 900 + Math.random() * 500, color: dark, size: 2 + Math.random() * 1.5, gravity: -6, grow: 2.6, alpha: 0.45 });
        if (unit.arch === 'burn' && Math.random() < 0.35) {
          this.particles.push({ x, y: y + 2, vx: (Math.random() - 0.5) * 12, vy: -30 - Math.random() * 20, life: 0, ttl: 220 + Math.random() * 120, color: 0xff9f43, size: 1.5 + Math.random(), gravity: 40, grow: 0.4 });
        }
      }
    }
  }

  /**
   * Damage stages on a building in battle: below 60 % cracks, below 30 % also smoke and a flicker of fire.
   * Drawn over the sprite from the structure HP the sim already keeps; nothing extra is stored.
   */
  private damageStages(g: Graphics, deltaMs: number): void {
    const b = this.game.battle;
    if (!b) return;
    for (const st of b.structures.values()) {
      if (!st.alive || st.hp * 10 >= st.maxHp * 6) continue;
      const r = this.cellRect({ x: st.index % b.W, y: Math.floor(st.index / b.W) });
      const cx = r.x + r.w / 2;
      const cy = r.y + r.h / 2;
      // Cracks: three short dark strokes seeded by the cell so they do not jitter.
      const seed = st.index * 7919;
      for (let k = 0; k < 3; k++) {
        const ax = r.x + 8 + ((seed * (k + 3)) % 97) / 97 * (r.w - 16);
        const ay = r.y + 8 + ((seed * (k + 5)) % 89) / 89 * (r.h - 20);
        const dx = 4 + ((seed * (k + 7)) % 5);
        const dy = 3 - ((seed * (k + 11)) % 7);
        g.moveTo(ax, ay).lineTo(ax + dx, ay + dy).lineTo(ax + dx * 1.6, ay + dy + 5).stroke({ width: 1.5, color: 0x1f1a17, alpha: 0.7 });
      }
      if (st.hp * 10 < st.maxHp * 3) {
        // Fire flicker at the base of the building and smoke rising from it.
        const f = 0.6 + 0.4 * Math.sin(this.now / 60 + st.index);
        g.ellipse(cx - 6, cy + 8, 5 * f, 7 * f).fill({ color: 0xff7043, alpha: 0.75 });
        g.ellipse(cx - 6, cy + 6, 3 * f, 4 * f).fill({ color: 0xffd166, alpha: 0.85 });
        this.smokeAcc += deltaMs;
        if (this.smokeAcc > 110 && this.particles.length < 240) {
          this.smokeAcc = 0;
          this.particles.push({ x: cx - 6 + (Math.random() - 0.5) * 6, y: cy, vx: (Math.random() - 0.5) * 10, vy: -18, life: 0, ttl: 800 + Math.random() * 400, color: 0x3d3d44, size: 2.5, gravity: -8, grow: 2.4, alpha: 0.55 });
        }
      }
    }
  }

  private soldierArch(ownerId: number): ArchetypeId {
    const s = this.game.state;
    for (const cs of s.grid) if (cs.kind === 'unit' && cs.unit.id === ownerId) return cs.unit.arch;
    // Owner already destroyed: fall back to the generic fighter look.
    return 'barracks';
  }

  private renderStrip(): void {
    this.strip.removeChildren();
    const s = this.game.state;
    const b = this.game.battle;
    // The panel belongs to the strip content, so the end screen does not show an empty box.
    if (!b && s.phase !== 'placing' && s.phase !== 'perk') return;
    const panel = new Graphics()
      .roundRect(8, L.stripY, L.W - 16, L.stripH, 10)
      .fill({ color: COLORS.panel, alpha: 0.92 })
      .stroke({ width: 1, color: COLORS.panelLine });
    panel.eventMode = 'none';
    this.strip.addChild(panel);
    const x0 = 18;
    const y0 = L.stripY + 8;
    if (b) {
      let alive = 0;
      for (const so of b.soldiers) if (so.alive && !so.hero) alive++;
      const waveText = s.endless ? `${this.t('wave')} ${s.wave} · ∞` : `${this.t('wave')} ${s.wave}/${s.totalWaves}`;
      const title = label(`${waveText}   ·   ${b.kills} ☠   ·   ${this.t('soldier_cap', { n: alive, max: b.soldierCap })}`, 14, COLORS.text, 'bold');
      title.x = x0;
      title.y = y0;
      const info = label(this.t('lost_structures', { n: b.lostStructures }), 11, b.lostStructures > 0 ? COLORS.danger : COLORS.textDim);
      info.x = x0;
      info.y = y0 + 24;
      this.strip.addChild(title, info);
      return;
    }

    const sel = this.selectedCell;
    if (sel) {
      const cs = s.grid[sel.y * s.width + sel.x];
      if (cs && cs.kind === 'unit') {
        const st = effectiveStats(cs.unit, this.game.balance, computeMods(s.perks), s.cardLevels[cs.unit.arch], s.skills);
        const th = this.theme.units[cs.unit.arch];
        const name = label(`${th.name[this.locale]} · ${this.t('level')} ${cs.unit.level}`, 13, hexToNum(th.color), 'bold');
        name.x = x0;
        name.y = y0;
        const parts: string[] = [this.t('hp', { n: st.hp })];
        if (st.cooldown > 0) parts.push(this.t('dmg', { n: st.dmg }), this.t('range', { n: (st.range / 1000).toFixed(1) }), this.t('dps', { n: Math.round((st.dmg * 20) / st.cooldown) }));
        if (st.soldier) parts.push(this.t('soldier', { name: this.theme.soldiers[cs.unit.arch]?.name[this.locale] ?? '' }), `${st.spawnMax} × ${st.soldier.hp} HP`);
        if (st.heal > 0 && st.healInterval > 0) parts.push(this.t('heal', { n: ((st.heal * 20) / st.healInterval).toFixed(1) }));
        if (st.income > 0) parts.push(this.t('income', { n: st.income }));
        const stats = label(parts.join(' · '), 9.5, COLORS.textDim);
        stats.x = x0;
        stats.y = y0 + 19;
        stats.style.wordWrap = true;
        stats.style.wordWrapWidth = L.W - 150;
        this.strip.addChild(name, stats);
        // Merge traits: what this level already does, and what the next trait level brings.
        const active = unitTraits(cs.unit, this.game.balance);
        const next = nextTrait(cs.unit, this.game.balance);
        const bits = active.map((tr) => this.t('trait_now', { name: this.t(`trait_${tr}`), desc: this.t(`trait_${tr}_desc`) }));
        if (next) bits.push(this.t('trait_at', { n: next.level, name: this.t(`trait_${next.trait}`) }));
        const traits = label(bits.join(' · '), 9.5, active.length > 0 ? COLORS.gold : COLORS.textDim);
        traits.x = x0;
        // The stats line may wrap; the trait line sits right under it, never on top of it.
        traits.y = Math.min(y0 + 36, stats.y + stats.height + 1);
        traits.style.wordWrap = true;
        traits.style.wordWrapWidth = L.W - 150;
        this.strip.addChild(traits);
        const price = sellValue(s, cs.unit, this.game.balance);
        const sell = this.button(this.t('sell_for', { gold: price }), 0x6b3f3f, () => {
          const cell = sel;
          this.selectedCell = null;
          if (this.game.act({ t: 'sell', cell })) sound.play('sell');
        }, 110, 30, 11);
        sell.x = L.W - 18 - 110;
        sell.y = y0 + 6;
        this.strip.addChild(sell);
        return;
      }
      if (cs && cs.kind === 'closed' && this.isClearableRock(sel)) {
        // A rock is selected: what the gold buys, in the enemy's terms.
        const idx = sel.y * s.width + sel.x;
        const real = previewPaths(s);
        const after = this.paths;
        const a = real.routes[0]?.length ?? 0;
        const b = after.routes[0]?.length ?? 0;
        const shorter = b > 0 && b < a;
        const notes: string[] = [shorter ? this.t('rock_path_shorter', { a, b }) : this.t('rock_path_same')];
        if (after.routes.some((r) => r.includes(idx))) notes.push(this.t('rock_on_path'));
        if (real.breakCells.length > 0 && after.breakCells.length === 0) notes.push(this.t('rock_unblocks'));
        const canPay = s.gold >= cs.cost;
        const name = label(`${this.t('rock_title')} · ${cs.cost} ◆`, 13, canPay ? COLORS.gold : COLORS.textDim, 'bold');
        name.x = x0;
        name.y = y0;
        const info = label(notes.join(' · '), 9.5, shorter ? COLORS.danger : COLORS.textDim);
        info.x = x0;
        info.y = y0 + 19;
        info.style.wordWrap = true;
        info.style.wordWrapWidth = L.W - 150;
        const hint = label(this.t('rock_tap_again'), 9.5, COLORS.textDim);
        hint.x = x0;
        hint.y = Math.min(y0 + 36, info.y + info.height + 1);
        this.strip.addChild(name, info, hint);
        const open = this.button(this.t('open_cost', { cost: cs.cost }), canPay ? 0x3a7d44 : COLORS.buttonDisabled, () => {
          if (canPay) this.clearRock(sel);
        }, 110, 30, 11);
        open.x = L.W - 18 - 110;
        open.y = y0 + 6;
        this.strip.addChild(open);
        return;
      }
    }

    const preview = this.game.wavePreview();
    const isLast = !s.endless && s.wave >= s.totalWaves;
    const title = label(
      s.endless
        ? this.t('next_wave_endless', { n: s.wave })
        : `${this.t('next_wave', { n: s.wave, total: s.totalWaves })}${isLast ? ` · ${this.t('last_wave')}` : ''}`,
      13,
      COLORS.text,
      'bold',
    );
    title.x = x0;
    title.y = y0;
    this.strip.addChild(title);

    let second: string;
    let secondColor: number = COLORS.textDim;
    if (this.paths.breakCells.length > 0) {
      const first = this.paths.breakCells[0]!;
      const cs = s.grid[first];
      const nm = cs && cs.kind === 'unit' ? this.theme.units[cs.unit.arch].name[this.locale] : '';
      second = this.t('break_warning', { name: nm });
      secondColor = COLORS.danger;
    } else if (s.modifiers.length > 0 && s.wave <= 2) {
      // A challenge opens by stating its rules where the legend would be.
      second = this.t('modifiers_line', { list: s.modifiers.map((m) => `${this.theme.modifiers[m].name[this.locale]} (${this.theme.modifiers[m].desc[this.locale]})`).join(' · ') });
      secondColor = 0xff9f43;
    } else if (s.wave <= 2) {
      second = `${this.t('legend_road')} · ${this.t('legend_grass')}\n${this.t('legend_hint')}`;
    } else if (preview.some((p) => p.arch === 'flyer' || p.arch === 'armored')) {
      // A wave with a special enemy explains it instead of listing counts: that is the decision the player must make.
      const special = preview.find((p) => p.arch === 'flyer') ?? preview.find((p) => p.arch === 'armored')!;
      const th = this.theme.enemies[special.arch];
      second = this.t('enemy_intro', { name: `${special.count} × ${th.name[this.locale]}`, desc: th.desc[this.locale] });
      secondColor = hexToNum(th.color);
    } else {
      const parts = preview.filter((p) => p.arch !== 'boss').map((p) => `${p.count} × ${this.theme.enemies[p.arch].name[this.locale]}`);
      if (preview.some((p) => p.arch === 'boss')) {
        const wave = waveFor(this.game.map, s.wave);
        const ability = wave?.spawns.find((g) => g.arch === 'boss')?.ability;
        const ab = ability ? this.theme.bossAbilities[ability] : null;
        parts.push(ab ? `${this.t('boss')} «${ab.name[this.locale]}»: ${ab.desc[this.locale]}` : `${this.t('boss')}: ${this.theme.enemies.boss.name[this.locale]}`);
        if (ab) secondColor = COLORS.danger;
      }
      second = parts.join(' · ');
    }
    const comp = label(second, 10, secondColor);
    comp.x = x0;
    comp.y = y0 + 22;
    comp.style.wordWrap = true;
    comp.style.wordWrapWidth = L.W - 110;
    this.strip.addChild(comp);

    // Enemy silhouettes, right side, and the income you will get for clearing this wave
    const g = new Graphics();
    let px = L.W - 26;
    for (const p of preview) {
      const th = this.theme.enemies[p.arch];
      const r = Math.min(th.radius, 8);
      drawEnemy(g, p.arch, px, y0 + 34, r, hexToNum(th.color));
      px -= r * 2 + 8;
    }
    this.strip.addChild(g);
    let income = waveGold(s, s.wave, this.game.balance);
    const mods = computeMods(s.perks);
    for (const { unit } of units(s)) {
      if (unit.arch === 'mine') income += effectiveStats(unit, this.game.balance, mods, s.cardLevels[unit.arch], s.skills).income;
    }
    const inc = label(this.t('income_per_wave', { n: income }), 11, COLORS.gold, 'bold');
    inc.anchor.set(1, 0);
    inc.x = L.W - 18;
    inc.y = y0 + 1;
    this.strip.addChild(inc);
  }

  private renderWall(): void {
    this.wall.removeChildren();
    const s = this.game.state;
    const b = this.game.battle;
    if (s.grid.some((c) => c.kind === 'base')) return; // the bunker on the board carries its own HP bar
    const hp = b ? b.wallHp : s.wallHp;
    const max = b ? b.wallMax : s.wallMax;
    const g = new Graphics();
    g.roundRect(8, L.wallY, L.W - 16, L.wallH, 6).fill(COLORS.hpBg).stroke({ width: 2, color: INK });
    const frac = max > 0 ? Math.max(0, hp / max) : 0;
    g.roundRect(8, L.wallY, (L.W - 16) * frac, L.wallH, 6).fill(frac > 0.3 ? COLORS.hp : COLORS.danger);
    this.wall.addChild(g);
    const tx = tileLabel(`${this.theme.wall.name[this.locale]} ${hp}/${max}`, 10);
    centerIn(tx, 0, L.wallY, L.W, L.wallH);
    this.wall.addChild(tx);
  }

  private renderShop(): void {
    this.shop.removeChildren();
    const s = this.game.state;
    const placing = s.phase === 'placing';
    const B = this.game.balance;
    for (let i = 0; i < s.shop.length; i++) {
      const arch = s.shop[i]!;
      const r = this.shopRect(i);
      const price = unitPrice(s, B, arch);
      const c = new Container();
      c.x = r.x;
      c.y = r.y;
      c.addChild(new Graphics().roundRect(0, 0, r.w, r.h, 8).fill(COLORS.grassB).stroke({ width: 1, color: COLORS.grassLine }));
      const tile = this.buildingTile({ id: -1, arch, level: 0 }, r.w, r.h, false);
      tile.alpha = placing && s.gold >= price ? 1 : 0.45;
      c.addChild(tile);
      if (placing) {
        c.eventMode = 'static';
        c.cursor = 'grab';
        c.on('pointerdown', (e) => this.beginDrag({ kind: 'shop', slot: i }, { id: -1, arch, level: 1 }, e));
      }
      this.shop.addChild(c);
      // Prices differ per building (a wall is cheap, a garage is not): each slot carries its own.
      const priceTx = tileLabel(`${price} ◆`, 11);
      priceTx.style.fill = s.gold >= price ? COLORS.gold : COLORS.textDim;
      centerIn(priceTx, r.x, r.y + r.h + 2, r.w, 14);
      this.shop.addChild(priceTx);
    }

    const rr = rerollPrice(s, B);
    const canReroll = placing && s.gold >= rr;
    const rb = this.rerollRect();
    const rerollBtn = this.button(`↻\n${rr}◆`, canReroll ? 0x3a7d9a : COLORS.buttonDisabled, () => {
      if (this.game.act({ t: 'reroll' })) sound.play('tap');
    }, rb.w, rb.h, 12);
    rerollBtn.x = rb.x;
    rerollBtn.y = rb.y;
    this.shop.addChild(rerollBtn);

    // Pocket: three slots for buildings bought early or lifted off the board (the reference has the same).
    for (let i = 0; i < s.pocket.length; i++) {
      const r = this.pocketRect(i);
      const u = s.pocket[i] ?? null;
      const c = new Container();
      c.x = r.x;
      c.y = r.y;
      c.addChild(new Graphics().roundRect(0, 0, r.w, r.h, 8).fill({ color: COLORS.wood, alpha: 0.6 }).stroke({ width: 1, color: COLORS.woodLight, alpha: 0.8 }));
      if (u) {
        const tile = this.buildingTile(u, r.w, r.h, true);
        tile.alpha = placing ? 1 : 0.6;
        c.addChild(tile);
        if (placing) {
          c.eventMode = 'static';
          c.cursor = 'grab';
          c.on('pointerdown', (e) => this.beginDrag({ kind: 'pocket', pocket: i }, u, e));
        }
      } else {
        const plus = tileLabel('+', 16);
        plus.style.fill = COLORS.woodLight;
        plus.alpha = 0.75;
        centerIn(plus, 0, 0, r.w, r.h);
        c.addChild(plus);
      }
      this.shop.addChild(c);
    }
    const p0 = this.pocketRect(0);
    const pl = this.pocketRect(s.pocket.length - 1);
    const cap = tileLabel(this.t('pocket'), 10);
    cap.style.fill = 0xe9d8b8;
    centerIn(cap, p0.x, p0.y + p0.h + 2, pl.x + pl.w - p0.x, 14);
    this.shop.addChild(cap);
  }

  private renderButtons(): void {
    this.buttons.removeChildren();
    const s = this.game.state;
    const placing = s.phase === 'placing';
    const fight = this.button(this.t('fight'), placing ? 0xe07b1a : COLORS.buttonDisabled, () => {
      if (placing) {
        haptic.medium();
        this.game.fight();
      }
    }, 196);
    fight.x = 14;
    fight.y = L.buttonsY;
    this.buttons.addChild(fight);
    const rp = wallRepairPrice(s, this.game.balance);
    const canRepair = placing && s.wallHp < s.wallMax && s.gold >= rp;
    const repair = this.button(
      `${this.t('repair_wall', { hp: this.game.balance.wallRepair.hp })} ${rp}◆`,
      canRepair ? 0x3a7d44 : COLORS.buttonDisabled,
      () => {
        if (this.game.act({ t: 'repair' })) sound.play('coin');
      },
      136,
      L.buttonH,
      12,
    );
    repair.x = L.W - 14 - 136;
    repair.y = L.buttonsY;
    this.buttons.addChild(repair);
  }

  private renderOverlay(): void {
    this.overlay.removeChildren();
    const s = this.game.state;
    if (s.phase === 'perk') this.renderPerkOverlay();
    else if (s.phase === 'won' || s.phase === 'lost') this.renderEndOverlay();
  }

  private dim(alpha: number): void {
    const dim = new Graphics().rect(-BLEED, -BLEED, L.W + BLEED * 2, L.H + BLEED * 2).fill({ color: 0x000000, alpha });
    dim.eventMode = 'static';
    this.overlay.addChild(dim);
  }

  private renderPerkOverlay(): void {
    const s = this.game.state;
    this.dim(0.72);
    const title = tileLabel(this.t('choose_perk'), 20);
    centerIn(title, 0, 120, L.W, 40);
    this.overlay.addChild(title);
    s.perkOffer.forEach((p, i) => {
      const th = this.theme.perks[p];
      const card = new Container();
      const w = L.W - 48;
      const h = 84;
      card.x = 24;
      card.y = 190 + i * (h + 14);
      card.addChild(new Graphics().roundRect(0, 0, w, h, 12).fill(COLORS.panel).stroke({ width: 2, color: COLORS.accent }));
      const icon = art.sprite(`perk:${p}`, 56, 56);
      const tx0 = icon ? 80 : 16;
      if (icon) {
        icon.x = 42;
        icon.y = h / 2;
        card.addChild(icon);
      }
      const name = label(th.name[this.locale], 17, COLORS.accent, 'bold');
      name.x = tx0;
      name.y = 14;
      const desc = label(th.desc[this.locale], 13, COLORS.text);
      desc.x = tx0;
      desc.y = 44;
      desc.style.wordWrap = true;
      desc.style.wordWrapWidth = w - tx0 - 16;
      card.addChild(name, desc);
      card.eventMode = 'static';
      card.cursor = 'pointer';
      card.on('pointertap', () => {
        haptic.light();
        if (this.game.act({ t: 'perk', index: i })) sound.play('buy');
      });
      this.overlay.addChild(card);
    });
  }

  private renderEndOverlay(): void {
    const s = this.game.state;
    this.dim(0.7);
    // A solid card behind the results: the board stays as a dimmed backdrop, the text stays readable.
    // Its height follows the content; the shape is drawn once the buttons' row is known.
    const panel = new Graphics();
    panel.eventMode = 'none';
    this.overlay.addChild(panel);
    const won = s.phase === 'won';
    const title = tileLabel(won ? this.t('won_title') : this.t('lost_title'), 26);
    title.style.fill = won ? COLORS.hp : COLORS.danger;
    centerIn(title, 0, 70, L.W, 40);
    if (this.hooks.challengeName) {
      const ch = label(this.t('challenge_run', { name: this.hooks.challengeName }), 12, 0xff9f43, 'bold');
      centerIn(ch, 0, 52, L.W, 16);
      this.overlay.addChild(ch);
    } else if (this.hooks.levelNumber !== null) {
      const lv = label(this.t('level_n', { n: this.hooks.levelNumber }), 12, COLORS.gold, 'bold');
      centerIn(lv, 0, 52, L.W, 16);
      this.overlay.addChild(lv);
      if (won) {
        const sub = this.submission;
        const stars = sub.kind === 'ready' && sub.result.ladder ? sub.result.ladder.stars : starsFor(s.wallHp, s.wallMax);
        const st = tileLabel('★★★'.slice(0, stars) + '☆☆☆'.slice(0, 3 - stars), 22);
        st.style.fill = COLORS.gold;
        centerIn(st, 0, 96, L.W, 24);
        this.overlay.addChild(st);
      }
    }
    const cleared = won ? s.totalWaves : s.wave - 1;
    const line1 = label(
      `${s.endless ? this.t('waves_cleared_endless', { n: cleared }) : this.t('waves_cleared', { n: cleared, total: s.totalWaves })}  ·  ${this.t('destroyed_total', { n: s.lostStructures })}`,
      13,
      COLORS.text,
    );
    centerIn(line1, 0, 112, L.W, 24);
    const line2 = label(this.t('score', { n: this.game.score() }), 18, COLORS.gold, 'bold');
    centerIn(line2, 0, 138, L.W, 28);
    this.overlay.addChild(title, line1, line2);

    let statusText = '';
    let statusColor: number = COLORS.textDim;
    const sub = this.submission;
    if (sub.kind === 'loading') statusText = this.t('submitting');
    else if (sub.kind === 'error') statusText = this.t('server_down');
    else if (sub.kind === 'challenge') {
      const r = sub.result;
      if (r.completed) {
        statusText = this.t('challenge_completed', { n: r.medals });
        statusColor = COLORS.gold;
      } else if (r.alreadyDone) statusText = this.t('challenge_already');
      else {
        statusText = this.t('challenge_failed', { n: r.goal });
        statusColor = COLORS.danger;
      }
      if (r.streak) statusText += `  ·  ${this.t('reward_streak', { n: r.streak.count, m: r.streak.medals })}`;
    } else if (sub.kind === 'ready') {
      statusText = this.t('rank', { rank: sub.result.rank, players: sub.result.players });
      statusColor = COLORS.accent;
      if (sub.result.best > sub.result.score) statusText += `  ·  ${this.t('best_today', { n: sub.result.best })}`;
    }
    if (statusText) {
      const st = label(statusText, 13, statusColor, 'bold');
      st.style.wordWrap = true;
      st.style.wordWrapWidth = L.W - 40;
      centerIn(st, 0, 172, L.W, 24);
      this.overlay.addChild(st);
    }

    // What the run paid into the profile: copies by building, hero XP, level-ups.
    const rewardLines: { text: string; color: number }[] = [];
    if (sub.kind === 'ready') {
      const rw = sub.result.rewards;
      if (rw) {
        const parts = (Object.keys(rw.copies) as ArchetypeId[])
          .filter((a) => (rw.copies[a] ?? 0) > 0)
          .sort((a, b) => (rw.copies[b] ?? 0) - (rw.copies[a] ?? 0))
          .map((a) => `${this.theme.units[a].name[this.locale]} ×${rw.copies[a]}`);
        if (parts.length > 0) rewardLines.push({ text: this.t('reward_copies', { list: parts.join(' · ') }), color: COLORS.gold });
        if (rw.heroXp > 0) rewardLines.push({ text: `${this.t('reward_hero_xp', { n: rw.heroXp })}${rw.medals ? `  ·  ${this.t('reward_medals', { n: rw.medals })}` : ''}`, color: COLORS.gold });
        const ups = rw.cardLevelUps.map((u) => this.t('level_up', { name: this.theme.units[u.arch].name[this.locale], n: u.level }));
        if (rw.heroLevelUp !== null) ups.push(this.t('hero_level_up', { n: rw.heroLevelUp }));
        if (ups.length > 0) rewardLines.push({ text: ups.join(' · '), color: COLORS.hp });
        if (rw.gear) {
          const name = `${this.t(`rarity_${rw.gear.rarity}`)} ${this.theme.gear[rw.gear.slot].name[this.locale]}`;
          rewardLines.push({ text: this.t('reward_gear', { name }), color: RARITY_COLORS[rw.gear.rarity] });
        } else if (rw.gearLost) {
          rewardLines.push({ text: this.t('inventory_full', { name: this.t('gear_hint').split('.')[0] ?? '' }), color: COLORS.danger });
        }
      } else {
        rewardLines.push({ text: this.t('rewards_exhausted'), color: COLORS.textDim });
      }
      if (sub.result.streak) rewardLines.push({ text: this.t('reward_streak', { n: sub.result.streak.count, m: sub.result.streak.medals }), color: 0xff9f43 });
      if (sub.result.referral) rewardLines.push({ text: this.t('reward_referral', { m: sub.result.referral.medals }), color: 0xff9f43 });
      if (sub.result.ladder?.firstWin) rewardLines.push({ text: this.t('first_win', { n: sub.result.ladder.medals }), color: COLORS.gold });
    }
    if (sub.kind === 'ready') {
      for (const done of sub.result.quests?.completed ?? []) {
        const q = done.quest;
        const text = q.kind === 'win_map' ? this.t('quest_win_map', { name: this.t(`${q.mapId ?? ''}_name`) }) : this.t(`quest_${q.kind}`, { n: q.target });
        rewardLines.push({ text: this.t('quest_completed_line', { text }), color: COLORS.hp });
      }
    }
    let rewardY = 196;
    for (const line of rewardLines) {
      const tx = label(line.text, 11, line.color, 'bold');
      tx.style.wordWrap = true;
      tx.style.wordWrapWidth = L.W - 40;
      tx.style.align = 'center';
      tx.anchor.set(0.5, 0);
      tx.x = L.W / 2;
      tx.y = rewardY;
      this.overlay.addChild(tx);
      rewardY += Math.ceil(tx.height) + 2;
    }
    const boardTop = rewardLines.length > 0 ? rewardY + 8 : 210;

    let contentBottom = boardTop;
    if (sub.kind === 'ready' && sub.result.leaderboard.length > 0) {
      const head = label(this.t('leaderboard'), 14, COLORS.textDim, 'bold');
      head.x = 28;
      head.y = boardTop;
      this.overlay.addChild(head);
      const rows = Math.min(10, sub.result.leaderboard.length, Math.floor((540 - boardTop - 26) / 26));
      contentBottom = boardTop + 26 + rows * 26;
      sub.result.leaderboard.slice(0, rows).forEach((e, i) => {
        const y = boardTop + 26 + i * 26;
        if (e.me) this.overlay.addChild(new Graphics().roundRect(20, y - 4, L.W - 40, 24, 6).fill({ color: COLORS.accent, alpha: 0.18 }));
        const name = label(`${i + 1}. ${e.name}${e.me ? ` (${this.t('you')})` : ''}`, 13, e.me ? COLORS.text : COLORS.textDim, e.me ? 'bold' : 'normal');
        name.x = 28;
        name.y = y;
        const score = label(String(e.score), 13, e.me ? COLORS.gold : COLORS.textDim, 'bold');
        score.anchor.set(1, 0);
        score.x = L.W - 28;
        score.y = y;
        this.overlay.addChild(name, score);
      });
    }

    const inChallenge = this.hooks.challengeName !== null;
    const nextLevel = won && !inChallenge && this.submission.kind === 'ready' ? this.submission.result.ladder?.next ?? null : null;
    const canShare = this.hooks.canShare() && !inChallenge && !nextLevel;
    const buttonsY = Math.min(560, Math.max(300, contentBottom + 18));
    const again = this.button(this.t('play_again'), COLORS.button, () => {
      this.submission = { kind: 'idle' };
      this.game.restart();
    }, canShare || inChallenge || nextLevel ? 160 : 220);
    again.x = canShare || inChallenge || nextLevel ? 16 : (L.W - 220) / 2;
    again.y = buttonsY;
    this.overlay.addChild(again);
    if (inChallenge) {
      const back = this.button(this.t('challenge_back'), 0x3a3f4b, () => this.hooks.onLeaveChallenge(), 160, L.buttonH, 13);
      back.x = L.W - 16 - 160;
      back.y = buttonsY;
      this.overlay.addChild(back);
    }
    if (nextLevel) {
      again.x = 16;
      const next = this.button(this.t('next_level'), 0x3a7d44, () => this.hooks.onNextLevel(nextLevel), 160, L.buttonH, 14);
      next.x = L.W - 16 - 160;
      next.y = buttonsY;
      this.overlay.addChild(next);
    }
    if (canShare) {
      const share = this.button(this.t('share'), 0x2a9d8f, () => this.hooks.onShare(), 160);
      share.x = L.W - 16 - 160;
      share.y = buttonsY;
      this.overlay.addChild(share);
    }
    panel.roundRect(10, 52, L.W - 20, buttonsY + L.buttonH + 16 - 52, 16).fill({ color: COLORS.panel, alpha: 0.94 }).stroke({ width: 2, color: COLORS.panelLine });
  }

  // ---------- drag & drop ----------

  private beginDrag(src: { kind: DragSource['kind']; slot?: number; cell?: Cell; pocket?: number }, unit: Unit, e: FederatedPointerEvent): void {
    if (this.game.state.phase !== 'placing') return;
    if (this.drag) this.drag.ghost.destroy();
    if (this.selectedCell && this.isClearableRock(this.selectedCell)) this.selectedCell = null;
    const ghost = this.buildingTile(unit, this.cellPx(), this.cellPx(), src.kind !== 'shop');
    ghost.alpha = 0.88;
    ghost.pivot.set(this.cellPx() / 2, this.cellPx() / 2);
    const p = this.root.toLocal(e.global);
    ghost.x = p.x;
    ghost.y = p.y;
    this.dragLayer.addChild(ghost);
    let sellHint: Container | undefined;
    if (src.kind !== 'shop') {
      // Dragging a real building: the shop plank becomes the sell zone, the pocket stays open for a drop.
      const s = this.game.state;
      const sellW = this.pocketRect(0).x - 18;
      sellHint = new Container();
      sellHint.addChild(new Graphics().roundRect(10, L.shopY - 6, sellW, L.H - L.shopY - 4, 12).fill({ color: 0x6b3f3f, alpha: 0.85 }).stroke({ width: 2, color: COLORS.danger }));
      const tx = tileLabel(this.t('drop_to_sell', { gold: sellValue(s, unit, this.game.balance) }), 13);
      tx.style.wordWrap = true;
      tx.style.wordWrapWidth = sellW - 20;
      centerIn(tx, 10, L.shopY + 6, sellW, 64);
      sellHint.addChild(tx);
      this.dragLayer.addChildAt(sellHint, 0);
    }
    this.drag = { ...src, unit, ghost, moved: false, sellHint };
  }

  private onMove(e: FederatedPointerEvent): void {
    if (!this.drag) return;
    const p = this.root.toLocal(e.global);
    if (Math.abs(p.x - this.drag.ghost.x) > 4 || Math.abs(p.y - this.drag.ghost.y) > 4) this.drag.moved = true;
    this.drag.ghost.x = p.x;
    this.drag.ghost.y = p.y;
    const pi = this.pocketAtPoint(p.x, p.y);
    if (pi !== this.hoverPocket) {
      this.hoverPocket = pi;
      this.pocketMark?.destroy();
      this.pocketMark = null;
      if (pi !== null && (this.drag.kind !== 'pocket' || this.drag.pocket !== pi)) {
        const r = this.pocketRect(pi);
        this.pocketMark = new Graphics().roundRect(r.x - 3, r.y - 3, r.w + 6, r.h + 6, 10).stroke({ width: 3, color: COLORS.gold });
        this.dragLayer.addChildAt(this.pocketMark, 0);
      }
    }
    this.previewDrop(pi === null ? this.cellAtPoint(p.x, p.y) : null);
  }

  /**
   * Show how the enemy road would bend if the dragged building landed on `target`.
   * Teaches the meaning of dirt vs grass better than any tooltip.
   */
  private previewDrop(target: Cell | null): void {
    const d = this.drag;
    if (!d) return;
    const s = this.game.state;
    const key = target ? cellKey(target) : null;
    if (key === this.hoverKey) return;
    this.hoverKey = key;
    const over = target ? s.grid[target.y * s.width + target.x] : undefined;
    const mergeDrop = d.kind !== 'cell' && over !== undefined && over.kind === 'unit' && over.unit.arch === d.unit.arch && over.unit.level === d.unit.level;
    if (!target || !canBuildAt(s, target) || over === undefined || over.kind !== 'empty' || mergeDrop) {
      const real = previewPaths(s);
      if (JSON.stringify(real.routes) !== JSON.stringify(this.paths.routes)) {
        this.paths = real;
        this.renderGrid();
      }
      return;
    }
    // Hypothetical board: the unit sits on the target (and, for a move, has left its source cell).
    const grid = s.grid.slice();
    if (d.kind === 'cell' && d.cell) grid[d.cell.y * s.width + d.cell.x] = { kind: 'empty' };
    grid[target.y * s.width + target.x] = { kind: 'unit', unit: { id: -1, arch: 'barricade', level: 1 } };
    this.paths = previewPaths({ ...s, grid });
    this.renderGrid();
  }

  private onUp(e: FederatedPointerEvent): void {
    const d = this.drag;
    if (!d) {
      // A plain tap away from a selected rock ends its preview. The re-render waits a frame so a tap
      // on another rock (dispatched right after this handler) still reaches its node.
      const sel = this.selectedCell;
      if (sel && this.isClearableRock(sel)) {
        const p = this.root.toLocal(e.global);
        const cell = this.cellAtPoint(p.x, p.y);
        if (!cell || cell.x !== sel.x || cell.y !== sel.y) {
          this.selectedCell = null;
          this.renderQueued = true;
        }
      }
      return;
    }
    this.drag = null;
    this.hoverKey = null;
    this.hoverPocket = null;
    d.ghost.destroy();
    d.sellHint?.destroy();
    this.pocketMark?.destroy();
    this.pocketMark = null;
    const p = this.root.toLocal(e.global);
    const pocketIdx = this.pocketAtPoint(p.x, p.y);
    const target = pocketIdx === null ? this.cellAtPoint(p.x, p.y) : null;
    // Whatever happens next re-renders from the real state; drop the hypothetical corridor.
    this.paths = previewPaths(this.game.state);

    // Into a pocket slot: buy for later, or lift a building off the board (equal ones merge there too).
    if (pocketIdx !== null) {
      if (d.kind === 'shop' && d.slot !== undefined) {
        if (this.game.act({ t: 'stash', slot: d.slot, pocket: pocketIdx })) {
          haptic.light();
          sound.play('buy');
        }
        return;
      }
      if (d.kind === 'pocket' && d.pocket !== undefined && d.pocket !== pocketIdx) {
        const before = this.game.state.pocket[pocketIdx] ?? null;
        if (this.game.act({ t: 'pocket_move', from: d.pocket, to: pocketIdx })) {
          const after = this.game.state.pocket[pocketIdx] ?? null;
          if (before && after && after.level > before.level) {
            haptic.medium();
            sound.play('merge', { pitch: 1 + Math.min(6, after.level - 2) * 0.12 });
          } else haptic.light();
        }
        return;
      }
      if (d.kind === 'cell' && d.cell) {
        this.selectedCell = null;
        const before = this.game.state.pocket[pocketIdx] ?? null;
        if (this.game.act({ t: 'pocket', from: d.cell, pocket: pocketIdx })) {
          const after = this.game.state.pocket[pocketIdx] ?? null;
          if (before && after && after.level > before.level) {
            haptic.medium();
            sound.play('merge', { pitch: 1 + Math.min(6, after.level - 2) * 0.12 });
          } else haptic.light();
        }
        return;
      }
      return;
    }

    // Dropped on the shop plank: sell (the reference sells by dragging off the board too).
    if (!target && p.y >= L.shopY - 10) {
      if (d.kind === 'cell' && d.cell) {
        this.selectedCell = null;
        if (this.game.act({ t: 'sell', cell: d.cell })) {
          haptic.medium();
          sound.play('sell');
        }
        return;
      }
      if (d.kind === 'pocket' && d.pocket !== undefined) {
        if (this.game.act({ t: 'sell_pocket', pocket: d.pocket })) {
          haptic.medium();
          sound.play('sell');
        }
        return;
      }
    }

    // Pocket → board: place, or merge onto an equal building.
    if (d.kind === 'pocket' && d.pocket !== undefined) {
      if (!target) return;
      const s0 = this.game.state;
      const before = s0.grid[target.y * s0.width + target.x];
      const beforeLevel = before && before.kind === 'unit' ? before.unit.level : -1;
      if (this.game.act({ t: 'place', pocket: d.pocket, to: target })) {
        const after = this.game.state.grid[target.y * s0.width + target.x];
        const merged = after && after.kind === 'unit' && beforeLevel >= 0 && after.unit.level > beforeLevel;
        if (merged && after.kind === 'unit') {
          haptic.medium();
          this.pop(target, 1.28);
          this.mergeFx(target, after.unit);
        } else {
          haptic.light();
          sound.play('buy');
          this.pop(target);
        }
      }
      return;
    }

    if (d.kind === 'shop' && d.slot !== undefined) {
      const s0 = this.game.state;
      const before = target ? s0.grid[target.y * s0.width + target.x] : undefined;
      const wasUnit = before !== undefined && before.kind === 'unit';
      if (target && this.game.act({ t: 'buy', slot: d.slot, to: target })) {
        const after = this.game.state.grid[target.y * s0.width + target.x];
        if (wasUnit && after && after.kind === 'unit') {
          // Bought straight onto an equal building: that is a merge.
          haptic.medium();
          this.pop(target, 1.28);
          this.mergeFx(target, after.unit);
        } else {
          haptic.light();
          sound.play('buy');
          this.pop(target);
        }
      }
      return;
    }
    if (d.kind === 'cell' && d.cell) {
      const from = d.cell;
      if (target && (target.x !== from.x || target.y !== from.y)) {
        const s = this.game.state;
        const before = s.grid[target.y * s.width + target.x];
        const beforeLevel = before && before.kind === 'unit' ? before.unit.level : -1;
        this.selectedCell = null;
        if (this.game.act({ t: 'move', from, to: target })) {
          const after = this.game.state.grid[target.y * s.width + target.x];
          const merged = after && after.kind === 'unit' && beforeLevel >= 0 && after.unit.level > beforeLevel;
          if (merged && after.kind === 'unit') {
            haptic.medium();
            this.pop(target, 1.28);
            this.mergeFx(target, after.unit);
          } else haptic.light();
        }
        return;
      }
      const same = this.selectedCell && this.selectedCell.x === from.x && this.selectedCell.y === from.y;
      this.selectedCell = same || d.moved ? null : from;
      this.renderGrid();
      this.renderField();
      this.renderStrip();
    }
  }

  /** A merge deserves more than a pop: a ring in the building's colour, sparks, the new level, a rising chime. */
  private mergeFx(cell: Cell, unit: Unit): void {
    const r = this.cellRect(cell);
    const cx = r.x + r.w / 2;
    const cy = r.y + r.h / 2;
    const color = hexToNum(this.theme.units[unit.arch].color);
    this.flashes.push({ fx: cx, fy: cy, tx: cx, ty: cy, color, life: 0, ttl: 380, splashPx: r.w * 0.75 });
    this.burst(cx, cy, 0xffffff, 6);
    this.burst(cx, cy, color, 10);
    const gained = traitGainedAt(unit.level, this.game.balance);
    const traits = unitTraits(unit, this.game.balance);
    const last = traits[traits.length - 1];
    const text = gained && last ? this.t('merge_trait_float', { n: unit.level, name: this.t(`trait_${last}`) }) : this.t('merge_float', { n: unit.level });
    this.floatNumber(text, cx, cy - 14, COLORS.gold, gained ? 16 : 15);
    sound.play('merge', { pitch: 1 + Math.min(6, unit.level - 2) * 0.12 });
    if (gained && last) this.banner(this.t(`trait_${last}`), COLORS.gold, this.t(`trait_${last}_desc`), COLORS.text, 1600);
  }

  private pop(cell: Cell, from = 1.15): void {
    const node = this.cellNodes.get(cellKey(cell));
    if (!node) return;
    node.scale.set(from);
    this.tweens.push({ target: node, from, to: 1, life: 0, ttl: 180 });
  }
}
